import { findRecord, query } from '@warp-drive/utilities/json-api';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { executeSearchRequest } from 'tio-data/requests/search';
import { getSlrAlertsRequest } from 'tio-data/requests/person';
import type {
  default as CareerPathwaysApiService,
  EmployerContributions,
  StudentLoanSummary,
} from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type {
  default as EmployeeService,
  FutureContributionPayment,
} from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type IntlService from 'ember-intl/services/intl';
import type FeaturesService from 'tio-common/services/features';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { AccountingTransaction as AccountingTransactionModel } from 'tio-data/data/resources/accounting-transaction.type';
import type { LoanExtension as LoanModel } from 'tio-data/data/resources/loan.ext';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import {
  getCurrentDemoTasScenarioId,
  getCurrentDemoScenario,
  getCurrentDemoPslfScenarioId,
  getCurrentDemoTaPaymentModelId,
  getDemoUserState,
} from 'tio-employee/utils/career-pathways-demo-data';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import {
  type RecentApplication,
  ACTIVE_APPLICATION_STATES,
  ACTIVE_INSTANCE_STATES,
  INACTIVE_APPLICATION_STATES,
  INACTIVE_INSTANCE_STATES,
  isActiveInstanceState,
  isInRepayment,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';
import { filterEligibleTemplates } from 'tio-common/utils/tuition-assistance/filter-eligibile-templates';
import {
  dynamicEligibilityRequest,
  periodsOfEligibilityRequest,
} from 'tio-data/requests/tas-participant-actions';
import { accountingPreviewRequest } from 'tio-data/requests/employee-actions';
import { planTiersRequest } from 'tio-data/requests/plan-actions';
import type { DynamicEligibility, PeriodOfEligibility } from 'tio-common/types/tuition-assistance';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import {
  getBenefitYearMonthsElapsed,
  isInCurrentBenefitYear,
} from 'tio-employee/utils/benefit-year';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';

export type DataSourceType = 'live' | 'demo' | 'placeholder';

/**
 * Comprehensive tracking of data sources across all reimagined pages.
 * Each field tracks whether it comes from live backend data, demo data, or is a placeholder.
 *
 * DATA SOURCE GAPS (what needs backend work):
 * - careerCapitalScore: No backend API yet - needs career pathways service
 * - skills: No skills tracking in backend - needs career pathways service
 * - careerPlan: No career plan model in backend - needs career pathways service
 * - recommendations: No recommendation engine - needs AI service
 * - optimization: No AI optimizer - needs AI service to analyze best allocation
 */
export interface DataSourceInfo {
  // Employee/Profile Data
  employeeProfile: DataSourceType;
  employerName: DataSourceType;

  // Financial Data
  studentLoans: DataSourceType;
  employerContributions: DataSourceType;
  section127Allocation: DataSourceType;
  section127Used: DataSourceType;

  // Benefit Eligibility
  pslfEligibility: DataSourceType;
  tasEligibility: DataSourceType;

  // Career Data (all demo/placeholder until backend built)
  careerCapitalScore: DataSourceType;
  skills: DataSourceType;
  careerPlan: DataSourceType;
  recommendations: DataSourceType;

  // Optimizer (placeholder until AI built)
  optimization: DataSourceType;
}

/**
 * Live employee profile data extracted from session/models
 */
export interface LiveEmployeeProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  currentRole: {
    title: string;
    department: string;
    startDate: string;
    yearsInRole: number;
  };
  taBenefit: {
    annualMax: number;
    used: number;
    remaining: number;
    eligiblePrograms: number;
  };
  slrBenefit: {
    monthlyContribution: number;
    annualMax: number;
    yearToDate: number;
    lifetime: number;
  };
  pslfEligible: boolean;
  pslfPaymentCount: number;
}

/**
 * Live Section 127 allocation derived from plan data
 */
export interface LiveSection127Info {
  annualLimit: number;
  yearToDateUsed: number;
  remaining: number;
  loanAllocation: number;
  tuitionAllocation: number;
  isLiveData: boolean;
}

/**
 * Normalized contribution payment for the reimagined contribution timeline/table
 */
export interface ContributionPaymentRecord {
  id: string;
  /** Amount in dollars (not cents) */
  amount: number;
  /** Benefit period (month/year label) */
  benefitCycle: string;
  /** Paid date (ISO string) or null if still in progress */
  paidDate: string | null;
  /** Servicer account name */
  servicer: string;
  /** Payment status */
  status: 'sent' | 'in_progress' | 'pending' | 'resent' | 'failed';
  /** Whether this is a future payment */
  isFuture: boolean;
}

/**
 * Extract the year string from a payment record.
 * Prefers paidDate, falls back to benefitCycle (YYYY-MM format).
 */
export function getPaymentYear(payment: ContributionPaymentRecord): string | null {
  const dateStr = payment.paidDate || payment.benefitCycle;
  if (dateStr) {
    const date = parseLocalDate(dateStr);
    return Number.isNaN(date.getTime()) ? null : date.getFullYear().toString();
  }
  return null;
}

export const CONTRIBUTION_TRANSACTION_PAGE_SIZE = 250;
const DEMO_TAS_ANNUAL_LIMIT = 5250;

type ContributionTransactionsResult = {
  transactions: AccountingTransactionModel[];
  meta: Record<string, unknown>;
};

function parseLocalDate(value: string): Date {
  const dateOnlyMatch = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(value);
  if (dateOnlyMatch) {
    const [, year, month, day = '1'] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return new Date(value);
}

function getContributionPaymentDate(payment: ContributionPaymentRecord): Date {
  return parseLocalDate(payment.paidDate || payment.benefitCycle);
}

function getContributionTransactionsTotal(meta: Record<string, unknown>): number | null {
  const page = meta['page'];
  if (typeof page !== 'object' || page === null) {
    return null;
  }

  const total = (page as Record<string, unknown>)['total'];
  return typeof total === 'number' ? total : null;
}

export function isSuccessfulContributionPayment(payment: ContributionPaymentRecord): boolean {
  return !payment.isFuture && payment.status !== 'failed';
}

export function calculateContributionYearToDate(
  payments: ContributionPaymentRecord[],
  now = new Date()
): number {
  return payments
    .filter(isSuccessfulContributionPayment)
    .filter((payment) => isInCurrentBenefitYear(getContributionPaymentDate(payment), now))
    .reduce((total, payment) => total + (payment.amount || 0), 0);
}

export function calculateContributionLifetimeTotal(payments: ContributionPaymentRecord[]): number {
  return payments
    .filter(isSuccessfulContributionPayment)
    .reduce((total, payment) => total + (payment.amount || 0), 0);
}

export function normalizeContributionTransaction(
  txn: AccountingTransactionModel
): ContributionPaymentRecord {
  const lineItem = txn.accountingLineItem;
  const txnState = (txn.state || '').toLowerCase();
  let status: ContributionPaymentRecord['status'] = 'pending';

  if (txnState === 'failed' || txnState === 'returned') {
    status = 'failed';
  } else if (txnState === 'resent' || txnState === 'retried') {
    status = 'resent';
  } else if (txn.transactionDate) {
    status = 'sent';
  } else if (lineItem?.date) {
    status = 'in_progress';
  }

  return {
    id: txn.id ?? '',
    amount: (txn.amount || 0) / 100,
    benefitCycle: lineItem?.period || lineItem?.date || '',
    paidDate: txn.transactionDate || null,
    servicer: txn.servicer || '',
    status,
    isFuture: false,
  };
}

export function normalizeContributionTransactions(
  transactions: AccountingTransactionModel[]
): ContributionPaymentRecord[] {
  // Canceled transactions never moved money. Drop them before normalizing so
  // they stay out of totals, history, and payment counts — matching the
  // tio-payment summary payload (state NOT IN ('CANCELED', 'FAILED')) that
  // tio-admin's contribution totals are built on.
  return transactions
    .filter((txn) => (txn.state || '').toLowerCase() !== 'canceled')
    .map((txn) => normalizeContributionTransaction(txn));
}

export function normalizeFutureContributionPayment(
  payment: FutureContributionPayment,
  index: number
): ContributionPaymentRecord {
  return {
    id: `future-${index}`,
    amount: (payment.amount || 0) / 100,
    benefitCycle: payment.period || '',
    paidDate: null,
    servicer: '',
    status: 'pending',
    isFuture: true,
  };
}

function addMonths(date: Date, months: number, day = date.getDate()): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDayOfTargetMonth));
  return target;
}

function formatIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function formatBenefitCycle(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}`;
}

function buildDemoPayment(
  id: string,
  amount: number,
  monthOffset: number,
  servicer: string,
  status: ContributionPaymentRecord['status'],
  now: Date,
  paidDay = monthOffset === 0 ? Math.min(now.getDate(), 14) : 14
): ContributionPaymentRecord {
  const cycleDate = addMonths(now, -monthOffset, 1);
  const paidDate = addMonths(now, -monthOffset, paidDay);

  return {
    id,
    amount,
    benefitCycle: formatBenefitCycle(cycleDate),
    paidDate: formatIsoDate(paidDate),
    servicer,
    status,
    isFuture: false,
  };
}

export function buildDemoContributionPayments(now = new Date()): {
  demoPayments: ContributionPaymentRecord[];
  nextPayment: ContributionPaymentRecord;
} {
  const recentPayments = Array.from({ length: 10 }, (_, index) =>
    buildDemoPayment(`demo-${index + 1}`, 125, index, 'MOHELA', 'sent', now)
  );
  const historicalPayments = Array.from({ length: 25 }, (_, index) =>
    buildDemoPayment(`demo-historical-${index + 1}`, 100, index + 13, 'Nelnet', 'sent', now)
  );

  const demoPayments = [
    ...recentPayments,
    buildDemoPayment('demo-11-resent', 125, 10, 'MOHELA', 'resent', now, 28),
    buildDemoPayment('demo-11-failed', 125, 10, 'MOHELA', 'failed', now),
    buildDemoPayment('demo-12', 125, 11, 'MOHELA', 'sent', now),
    buildDemoPayment('demo-13', 125, 12, 'MOHELA', 'sent', now),
    ...historicalPayments,
  ].sort((a, b) => {
    const aDate = a.paidDate ? new Date(a.paidDate).getTime() : 0;
    const bDate = b.paidDate ? new Date(b.paidDate).getTime() : 0;
    return bDate - aDate;
  });

  const futureCycleDate = addMonths(now, 1, 1);
  const nextPayment: ContributionPaymentRecord = {
    id: 'demo-next',
    amount: 125,
    benefitCycle: formatBenefitCycle(futureCycleDate),
    paidDate: null,
    servicer: 'MOHELA',
    status: 'pending',
    isFuture: true,
  };

  return { demoPayments, nextPayment };
}

export type ActionItemIssueType =
  'account-setup' | 'account-ineligible' | 'plan-requirement' | 'default';

/** Alert from the tio-api SlrAlertService */
export interface SlrAlert {
  type: string;
  category: ActionItemIssueType;
  display_mode: 'single' | 'grouped';
  resolution_type: 'upload' | 'dropdown' | 'contact-support' | 'none';
  title: string;
  description: string;
  priority: number;
  metadata: Record<string, unknown>;
}

/** Response from GET /people/:id/slr-alerts */
export interface SlrAlertsResponse {
  page_state: string;
  alerts: SlrAlert[];
  contribution_account: {
    id: string;
    servicer_name: string | null;
    account_number: string | null;
    balance: number | null;
    is_pif: boolean;
    servicer_address_type: string | null;
    /** PARTIALLY_APPLIED means the account was created but no servicer match was found */
    status?: string;
  } | null;
}

export type SlrPageState =
  | 'active'
  | 'non-slr-never'
  | 'non-slr-former'
  | 'no-accounts'
  | 'all-pif'
  | 'waiting-period'
  | 'limit-annual'
  | 'limit-lifetime'
  | 'limit-annual-and-lifetime';

export interface ContributionActionTask {
  id: string;
  title: string;
  description: string;
  resolutionType: Extract<SlrAlert['resolution_type'], 'upload' | 'dropdown'>;
  issueLabels: string[];
  metadata?: Record<string, unknown>;
  successMessage?: string;
}

const VALID_PAGE_STATES: SlrPageState[] = [
  'active',
  'non-slr-never',
  'non-slr-former',
  'no-accounts',
  'all-pif',
  'waiting-period',
  'limit-annual',
  'limit-lifetime',
  'limit-annual-and-lifetime',
];

export function normalizeSlrPageState(raw: string | undefined): SlrPageState | undefined {
  if (!raw) return undefined;
  return VALID_PAGE_STATES.includes(raw as SlrPageState) ? (raw as SlrPageState) : undefined;
}

export interface ContributionActionItem {
  id: string;
  title: string;
  description: string;
  badge: string;
  badgeIntent: 'danger' | 'warning' | 'default';
  route: string;
  /** Categorizes the action item for icon selection per Figma Issue Type Icons */
  issueType: ActionItemIssueType;
  /** How the user resolves this issue (upload, dropdown, contact-support, none) */
  resolutionType?: SlrAlert['resolution_type'];
  /** Extra data from the API (e.g. available addresses for dropdown) */
  metadata?: Record<string, unknown>;
  /** Grouped resolution tasks shown inside one combined notification card */
  tasks?: ContributionActionTask[];
  /** Custom upload success message for this action item */
  successMessage?: string;
}

export function getActionItemIconBg(item: ContributionActionItem) {
  if (item.badgeIntent === 'danger') return 'tio-action-item-icon-bg--danger';
  if (item.badgeIntent === 'warning') return 'tio-action-item-icon-bg--warning';
  return 'tio-action-item-icon-bg--default';
}

export function getActionItemIconColor(item: ContributionActionItem) {
  if (item.badgeIntent === 'danger') return 'tio-action-item-icon-fg--danger';
  if (item.badgeIntent === 'warning') return 'tio-action-item-icon-fg--warning';
  return 'tio-action-item-icon-fg--default';
}

/**
 * Maps a PaymentValidationError key to an ActionItemIssueType.
 *
 * - account-setup:       issues the user can fix by uploading docs or linking accounts
 * - account-ineligible:  the linked loan type isn't eligible for the benefit
 * - plan-requirement:    plan/policy-level issues (limits, eligibility, plan not started)
 * - default:             everything else (name mismatch, stale data, loan status)
 */
const VALIDATION_KEY_TO_ISSUE_TYPE: Record<string, ActionItemIssueType> = {
  ACCOUNTS_UNPAYABLE: 'account-setup',
  NO_LOAN_BALANCE: 'account-setup',
  STALE_ACCOUNT: 'account-setup',
  INVALID_LOAN_TYPE: 'account-ineligible',
  AGGREGATION_INVALID_LOAN_TYPE: 'account-ineligible',
  EXCEEDED_LIMIT: 'plan-requirement',
  NOT_STARTED_PLAN: 'plan-requirement',
  TERMS_NOT_ACCEPTED: 'plan-requirement',
  INVALID_LOAN_STATUS: 'plan-requirement',
  NO_EMPLOYEE_TRANSACTIONS: 'default',
  NAME_MISMATCH: 'default',
};

const GROUPABLE_SLR_ALERT_TYPES = new Set([
  'blank-account-number',
  'no-loan-balance',
  'blank-payment-address',
]);

const UPDATED_LOAN_INFO_ALERT_TYPES = new Set([
  'blank-account-number',
  'no-loan-balance',
  'blank-payment-address',
]);

export interface ContributionYearlyPayment {
  year: string;
  total: number;
  accounts: { name: string; amount: number }[];
  paymentCount: number;
  sentCount: number;
  returnedCount: number;
  resentCount: number;
}

/**
 * Comprehensive contribution history data for the reimagined finance/history page
 */
export interface ContributionHistoryData {
  /** Past + future contribution payments, most recent first */
  payments: ContributionPaymentRecord[];
  /** The last completed payment */
  lastPayment: ContributionPaymentRecord | null;
  /** The next upcoming payment */
  nextPayment: ContributionPaymentRecord | null;
  /** Total lifetime contributions in dollars */
  lifetimeTotal: number;
  /** Number of completed payments */
  paymentCount: number;
  /** Year-to-date contributions in dollars */
  yearToDate: number;
  /** Whether SLR contributions are active */
  isSlrEligible: boolean;
  /** Account receiving contributions (name) */
  accountReceivingName: string | null;
  /** Whether user has multiple accounts (to show change button) */
  hasMultipleAccounts: boolean;
  /**
   * Legacy client-side account heuristics (employee.ts#accountAlerts),
   * passed through ONLY for the finance/history PaymentAlertCard banner —
   * no longer used to derive action items (see #9443).
   */
  accountAlerts: { content: string; uploadDocument?: boolean; addressSelect?: boolean }[];
  /** Payment validation errors on next payment */
  paymentValidationErrors: string[];
  /** Action items requiring user attention */
  actionItems: ContributionActionItem[];
  /** Payments grouped by year, sorted descending */
  paymentsByYear: ContributionYearlyPayment[];
  /**
   * The plan's configured per-cycle contribution in dollars (first tier by
   * fromCycle), or null when tier data is unavailable. Display fallback for
   * "next contribution" when the preview endpoint has no row.
   */
  scheduledMonthlyAmount: number | null;
  /** Whether this is live data */
  isLiveData: boolean;
  /** Page state from SLR alert service (active, waiting-period, limit-*, etc.) */
  pageState?: SlrPageState;
  /** Raw SLR alerts from the API */
  slrAlerts?: SlrAlert[];
  /** JSONAPI pagination metadata (page count, record count, etc.) */
  meta?: Record<string, unknown>;
}

/**
 * Live PSLF data from pslf-profile and pslf-form models.
 * Accounts/loans data comes from the same models used by qualifying-payment-tracker.
 */
export interface LivePslfData {
  currentEmployer: {
    name: string;
    isQualifying: boolean;
    startDate: string | null;
  } | null;
  forms: PslfFormModel[];
  accounts: LiveAccount[];
  hasCompletedEstimator: boolean;
  estimatorResults: {
    estimatedMonthlyPayment: number;
    estimatedForgivenessAmount: number;
    estimatedForgivenessDate: string;
    repaymentPlan: string;
  } | null;
  isLiveData: boolean;
}

export interface LiveAccount {
  id: string;
  institutionName: string;
  loans: LiveLoan[];
}

export interface LiveLoan {
  id: string;
  name: string;
  balance: number;
  interestRate: number;
  qualifyingPayments: number;
  repaymentPlan: string | null;
  isPaidOff: boolean;
  status: string | null;
}

/**
 * Map AccountModel[] (Ember Data) to the LiveAccount[] shape consumed by the v2 PSLF
 * dashboard. Exported so the PSLF debug path in the route can apply the same mapping
 * to mock accounts pushed into the store, instead of building a parallel mock shape
 * that drifts from the live one.
 */
export function accountsToLiveAccounts(accounts: AccountModel[]): LiveAccount[] {
  return accounts
    .filter((account) => account.hasNsldsPslfData)
    .map((account) => ({
      id: account.id as string,
      institutionName: account.institutionName ?? 'Unknown Institution',
      loans: account.nsldsPslfLoans.map((loan: LoanModel) => ({
        id: loan.id as string,
        name: loan.observation?.['loan-name'] ?? `Loan ${loan.id ?? ''}`,
        balance: loan.observation?.['principal-balance'] ?? 0,
        interestRate: loan.observation?.['interest-rate'] ?? 0,
        qualifyingPayments: loan.observation?.['forgiveness-program-qualified'] ?? 0,
        repaymentPlan: loan.observation?.['repayment-plan-derived'] ?? null,
        isPaidOff: loan.observation?.['is-paid-off'] ?? false,
        status: loan.observation?.['status'] ?? null,
      })),
    }))
    .filter((account) => account.loans.length > 0);
}

/**
 * Eligible program template for display in the Eligible Benefits section
 */
export interface EligibleTemplate {
  id: string;
  name: string;
  description: string | null;
  isDirectPayProgram: boolean;
  isEnrolled: boolean;
  isTrial: boolean;
  typeClassification: string;
}

/**
 * Live TAS (Tuition Assistance) data
 */
export interface LiveTasData {
  isEligible: boolean;
  participantState: string | null;
  programs: LiveTasProgram[];
  applications: LiveTasApplication[];
  summary: {
    totalApproved: number;
    totalPaid: number;
    pendingCount: number;
    activeCount: number;
    completedCount: number;
  };
  isLiveData: boolean;
}

export interface LiveTasProgram {
  id: string;
  name: string;
  state: string;
  institution: string | null;
  major: string | null;
  startDate: string | null;
  updatedAt: string | null;
  typeClassification: string | null;
  dependentId: string | null;
  dependentName: string | null;
}

export interface LiveTasApplication {
  id: string;
  recordType: 'application' | 'instance';
  programInstanceId: string | null;
  programTemplateId: string | null;
  name: string;
  state: string;
  paymentState: string;
  approvedTotal: number;
  requestedTotal: number;
  paidTotal: number;
  courseCashPreApprovedTotal: number | null;
  courseCashFinalApprovedTotal: number | null;
  courseCashUsed: number | null;
  courseCashReserved: number | null;
  coursesBeginDate: string | null;
  coursesEndDate: string | null;
  createdAt: string;
  updatedAt: string | null;
  programName: string | null;
  dependentId: string | null;
  dependentName: string | null;
}

/**
 * A proxy object that mimics the TasApplicationModel interface for demo mode.
 * Used by the show route to avoid making real API calls for demo data.
 */
export interface DemoTasApplicationProxy {
  id: string;
  state: string;
  paymentState: string;
  approvedTotal: number;
  createdAt: string;
  displayName: string;
  activeCourses: unknown[];
  tasCourses: unknown[];
  tasAssets: unknown[];
  tasProgramInstance: {
    id: string;
    tasProgramTemplate: {
      programName: string;
      typeClassification: string;
      courseApprovalRequired: boolean;
    };
    fields: Record<string, unknown>;
  } | null;
  fields: Record<string, unknown>;
  applicationName: string;
  programName: string;
  coursesBeginDate: string;
  transitionLog: unknown[];
  customFields: unknown[];
  paymentStateLog: unknown[];
  approvedTotalLog: unknown[];
  coursesApproval: unknown;
  evidenceApproval: unknown;
  isDraft: boolean;
  isApproved: boolean;
  tags: unknown[];
  attachments: unknown[];
  _isDemoProxy: true;
}

export default class ReimaginedDataService extends Service {
  @service declare store: Store;
  @service declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare partner: PartnerService;
  @service declare intl: IntlService;
  @service declare features: FeaturesService;
  @service declare demoMode: DemoModeService;

  private tasSearchCache?: {
    employeeId: string;
    companyId: string | null;
    fetchedAt: number;
    docs: RecentApplication[];
  };

  private tasDependentSearchCache?: {
    employeeId: string;
    companyId: string | null;
    fetchedAt: number;
    docs: RecentApplication[];
  };

  private contributionTransactionsCache?: {
    employeeId: string | null;
    fetchedAt: number;
    promise: Promise<ContributionTransactionsResult>;
  };

  private readonly tasSearchCacheTtlMs = 30_000;
  private readonly contributionTransactionsCacheTtlMs = 5_000;

  /**
   * Tracked state for data audit mode. When enabled, data source badges are shown
   * and placeholder sections are highlighted with marching ants.
   */
  @tracked isDataAuditEnabled = this.initDataAuditState();

  /**
   * Tracked state for data source docs panel visibility
   */
  @tracked isDocsOpen = false;

  /**
   * The section ID to scroll to when opening docs
   */
  @tracked docsSectionId?: string;

  private initDataAuditState() {
    if (typeof window === 'undefined') {
      return false;
    }
    // Read from localStorage for persistence across page refreshes
    const stored = localStorage.getItem('reimagined-data-audit');
    const isEnabled = stored === 'true';
    // Apply the body class on init to match stored state
    if (isEnabled) {
      document.body.classList.add('reimagined-data-audit');
    }
    return isEnabled;
  }

  /**
   * Toggle data audit mode. This shows/hides data source badges and
   * highlights placeholder sections.
   */
  toggleDataAudit = () => {
    this.isDataAuditEnabled = !this.isDataAuditEnabled;
    if (typeof window !== 'undefined') {
      document.body.classList.toggle('reimagined-data-audit', this.isDataAuditEnabled);
      // Persist to localStorage for page refresh persistence
      localStorage.setItem('reimagined-data-audit', String(this.isDataAuditEnabled));
    }
  };

  /**
   * Open the data source documentation panel, optionally scrolling to a section
   */
  openDocs = (sectionId?: string) => {
    this.docsSectionId = sectionId;
    this.isDocsOpen = true;
  };

  /**
   * Close the data source documentation panel
   */
  closeDocs = () => {
    this.isDocsOpen = false;
    this.docsSectionId = undefined;
  };

  /**
   * Controls whether data source badges (Live Data, Demo Data, Not Connected)
   * are shown on reimagined UI components.
   *
   * Badge visibility is controlled by the Data Audit toggle (warning icon) in the header.
   * Can also be controlled via URL param ?show_badges=true/false.
   *
   * - In demo mode: badges always hidden
   * - In live mode + data audit OFF: badges hidden
   * - In live mode + data audit ON: badges shown
   */
  get showDataSourceBadges() {
    if (typeof window === 'undefined') {
      return false;
    }

    // URL param overrides everything
    const params = new URLSearchParams(window.location.search);
    const badgeParam = params.get('show_badges');
    if (badgeParam === 'true') return true;
    if (badgeParam === 'false') return false;

    // In demo mode, never show badges (demos are for sales/marketing)
    if (this.isDemoMode) {
      return false;
    }

    // In live mode, show badges only when data audit is enabled (tracked property)
    return this.isDataAuditEnabled;
  }

  get isDemoMode() {
    // Delegate to the single gated chokepoint (isAvailable && active) so this
    // service shares ONE notion of "in demo" with the request handler and fetch
    // seams — never a raw, ungated read that could disagree and strand a
    // half-demo / half-live data path.
    return this.demoMode.isActive;
  }

  private loadContributionTransactions(): Promise<ContributionTransactionsResult> {
    const employeeId = this.sessionContext.currentEmployee?.id ?? null;
    const now = Date.now();

    if (
      this.contributionTransactionsCache?.employeeId === employeeId &&
      now - this.contributionTransactionsCache.fetchedAt < this.contributionTransactionsCacheTtlMs
    ) {
      return this.contributionTransactionsCache.promise;
    }

    const promise = this.fetchAllContributionTransactions();

    this.contributionTransactionsCache = {
      employeeId,
      fetchedAt: now,
      promise,
    };

    promise.catch(() => {
      if (this.contributionTransactionsCache?.promise === promise) {
        this.contributionTransactionsCache = undefined;
      }
    });

    return promise;
  }

  private queryContributionTransactionsPage(
    pageNumber: number
  ): Promise<ContributionTransactionsResult> {
    return this.employee.queryContributionTransactions({
      page: {
        size: CONTRIBUTION_TRANSACTION_PAGE_SIZE,
        number: pageNumber,
      },
      sort: '-transaction-date',
    });
  }

  private async fetchAllContributionTransactions(): Promise<ContributionTransactionsResult> {
    const firstPage = await this.queryContributionTransactionsPage(1);
    const total = getContributionTransactionsTotal(firstPage.meta);
    if (!total || total <= firstPage.transactions.length) {
      return firstPage;
    }

    const pageCount = Math.ceil(total / CONTRIBUTION_TRANSACTION_PAGE_SIZE);
    const remainingPages = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, index) =>
        this.queryContributionTransactionsPage(index + 2)
      )
    );

    return {
      transactions: [
        ...firstPage.transactions,
        ...remainingPages.flatMap((result) => result.transactions),
      ],
      meta: firstPage.meta,
    };
  }

  // ===========================================================================
  // FEATURE FLAGS — centralized scenario-aware feature visibility
  //
  // In demo mode these come from the scenario. In live mode they come from
  // partner config / employee data. Every reimagined page should use these
  // instead of inlining its own checks.
  // ===========================================================================

  /**
   * Whether Tuition Assistance should be shown to this user.
   * Demo mode → scenario.features.tuitionAssistance
   * Live mode → partner has TAS enabled
   */
  get showTuitionAssistance(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.tuitionAssistance;
    }
    return this.partner.hasNewTuitionAssistance;
  }

  /**
   * Whether Student Loan Repayment content should be shown.
   * Demo mode → scenario.features.studentLoanRepayment
   * Live mode → partner has contribution plans
   */
  get showStudentLoanRepayment(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.studentLoanRepayment;
    }
    return this.partner.hasContributions;
  }

  /**
   * Whether PSLF content should be shown.
   * Demo mode → scenario.features.pslf
   * Live mode → partner has PSLF module enabled
   */
  get showPslf(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.pslf;
    }
    return !!this.partner.isPslfEnabled;
  }

  /**
   * Whether career pathways content should be shown.
   */
  get showCareerPathways(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.careerPathways;
    }
    return true;
  }

  /**
   * Whether financial wellness content should be shown.
   * Demo mode → scenario features
   * Live mode → partner has wellness plans
   */
  get showFinancialWellness(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.financialWellness;
    }
    return this.partner.hasWellness;
  }

  /**
   * The full current scenario (for messaging, userState, etc.).
   * Only meaningful in demo mode — returns the 'full' scenario as fallback.
   */
  get currentScenario() {
    return getCurrentDemoScenario();
  }

  async fetchDashboardData() {
    return this.careerPathwaysApi.fetchDashboardData();
  }

  async fetchCareerCapitalScore() {
    return this.careerPathwaysApi.fetchCareerCapitalScore();
  }

  async fetchFinancialSnapshot() {
    if (this.isDemoMode) {
      return this.careerPathwaysApi.fetchFinancialSnapshot();
    }

    const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
    const [loansData, contributionsData] = await Promise.all([
      this.fetchLoansData(),
      this.fetchEmployerContributions(),
    ]);

    // The base snapshot is career-pathways mock data — overlay the live
    // contribution totals so live users never see its fixture amounts
    // (e.g. the hardcoded $8,750 lifetime).
    const employerContributions = contributionsData.contributions;

    if (!loansData.hasLinkedLoans) {
      return { ...snapshot, employerContributions };
    }

    const totalBalance = loansData.loans.reduce((sum, loan) => sum + loan.balance, 0);
    const totalMonthlyPayment = loansData.loans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
    const weightedRate = loansData.loans.reduce(
      (sum, loan) => sum + loan.balance * loan.interestRate,
      0
    );
    const interestRate = totalBalance > 0 ? Number((weightedRate / totalBalance).toFixed(1)) : 0;

    const payoffDates = loansData.loans.map((loan) => loan.estimatedPayoff).filter((date) => date);
    const estimatedPayoffDate =
      payoffDates.length > 0
        ? (payoffDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ??
          snapshot.studentLoans.estimatedPayoffDate)
        : snapshot.studentLoans.estimatedPayoffDate;

    const uniqueServicers = Array.from(new Set(loansData.loans.map((loan) => loan.servicer)));
    const servicer =
      uniqueServicers.length === 1 ? (uniqueServicers[0] ?? 'Loan Servicer') : 'Multiple servicers';

    const studentLoans: StudentLoanSummary = {
      ...snapshot.studentLoans,
      totalBalance,
      monthlyPayment: totalMonthlyPayment,
      interestRate,
      estimatedPayoffDate,
      servicer,
      paymentsMade: snapshot.studentLoans.paymentsMade ?? 0,
    };

    return {
      ...snapshot,
      studentLoans,
      employerContributions,
    };
  }

  async fetchScoreHistory(months: number) {
    return this.careerPathwaysApi.fetchScoreHistory(months);
  }

  async fetchSection127Recommendation() {
    return this.careerPathwaysApi.fetchSection127Recommendation();
  }

  async fetchLoansData() {
    if (this.isDemoMode) {
      return {
        isDemoMode: true,
        loans: [] as LoanAccount[],
        hasLinkedLoans: false,
        activeAccounts: [] as AccountModel[],
        accountReceivingContributions: null as AccountModel | null,
        personId: null as string | null,
      };
    }

    const personId = this.sessionContext.person?.id;
    if (!personId) {
      return {
        isDemoMode: false,
        loans: [] as LoanAccount[],
        hasLinkedLoans: false,
        activeAccounts: [] as AccountModel[],
        accountReceivingContributions: null as AccountModel | null,
        personId: null as string | null,
      };
    }

    const { content: personContent } = (await this.store.request(
      findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans,accounts.postal-address',
        reload: true,
      })
    )) as { content: { data: PersonModel } };
    const person = personContent.data;

    const activeAccounts = person.activeAccounts || [];
    const accountReceivingContributionsId = person.accountReceivingContributionsId;

    const loans = activeAccounts
      .map((account: AccountModel): LoanAccount => {
        const activeLoans = account.activeLoans || [];
        const loanCount = activeLoans.length;

        const originalBalance = activeLoans.reduce(
          (sum, loan: LoanModel) => sum + (loan.originalAmount || 0),
          0
        );
        const balance = activeLoans.reduce(
          (sum, loan: LoanModel) => sum + (loan.currentBalance || 0),
          0
        );

        const monthlyPayment =
          account.loanMinMonthlyPayment || account.providerRegularMonthlyPayment || 0;

        const nextPaymentDate =
          activeLoans.find((loan) => loan.nextPaymentDate)?.nextPaymentDate ||
          account.providerAttemptAt ||
          new Date().toISOString();

        const interestRate = Number(account.loanInterestAverage || 0);

        const loanClass = activeLoans[0]?.loanClass?.toLowerCase() || '';
        const accountType = loanClass.includes('private')
          ? 'private'
          : loanClass.includes('refinance')
            ? 'refinanced'
            : 'federal';

        // Estimate payoff date from balance, payment, and rate instead of using
        // the next payment date (which is just when the next bill is due, NOT payoff)
        const estimatedPayoff = this.estimatePayoffDate(balance, monthlyPayment, interestRate);

        const postalAddress = account.postalAddress;
        const addressParts = postalAddress
          ? [
              postalAddress.addressLine1,
              postalAddress.addressLine2,
              [
                [postalAddress.city, postalAddress.state].filter(Boolean).join(', '),
                postalAddress.postalCode,
              ]
                .filter(Boolean)
                .join(' '),
            ]
              .filter(Boolean)
              .join(' ')
          : '';

        return {
          id: account.id ?? '',
          name: activeLoans[0]?.loanTypeDisplayName || activeLoans[0]?.name || 'Student Loan',
          accountNumber: account.accountNumber
            ? account.accountNumber.length >= 4
              ? '****' + account.accountNumber.slice(-4)
              : account.accountNumber
            : '',
          servicer: account.institutionName || account.observation?.provider || 'Loan Servicer',
          balance,
          originalBalance: originalBalance || balance,
          interestRate,
          monthlyPayment,
          nextPaymentDate,
          estimatedPayoff,
          isReceivingEmployerHelp: account.id === accountReceivingContributionsId,
          loanCount,
          accountType,
          paymentAddress: addressParts,
        };
      })
      .filter((loan) => loan.loanCount > 0);

    return {
      isDemoMode: false,
      loans,
      hasLinkedLoans: loans.length > 0,
      activeAccounts,
      accountReceivingContributions: person.accountReceivingContributions ?? null,
      personId,
    };
  }

  /**
   * Simple amortization-based payoff date estimate.
   * Uses balance, monthly payment, and interest rate to project when the loan
   * will be paid off. Falls back to 10 years if inputs are insufficient.
   */
  private estimatePayoffDate(balance: number, monthlyPayment: number, annualRate: number) {
    if (balance <= 0) {
      return new Date().toISOString();
    }

    // If payment is too small to cover interest, estimate 10 years as fallback
    const monthlyRate = annualRate / 100 / 12;
    const monthlyInterest = balance * monthlyRate;

    if (monthlyPayment <= 0 || monthlyPayment <= monthlyInterest) {
      const fallback = new Date();
      fallback.setFullYear(fallback.getFullYear() + 10);
      return fallback.toISOString();
    }

    // Standard amortization: n = -log(1 - (r * PV / PMT)) / log(1 + r)
    let months: number;
    if (monthlyRate > 0) {
      months = Math.ceil(
        -Math.log(1 - (monthlyRate * balance) / monthlyPayment) / Math.log(1 + monthlyRate)
      );
    } else {
      months = Math.ceil(balance / monthlyPayment);
    }

    // Cap at 30 years (360 months) to avoid unreasonable dates
    months = Math.min(months, 360);

    const payoffDate = new Date();
    payoffDate.setMonth(payoffDate.getMonth() + months);
    return payoffDate.toISOString();
  }

  /**
   * Fetches employer contribution data from the employee service
   * Returns live data if available, otherwise falls back to demo data
   */
  async fetchEmployerContributions(): Promise<{
    contributions: EmployerContributions;
    isLiveData: boolean;
  }> {
    // May still be loading: contributionSummary comes from an un-awaited
    // trackedFunction, and in demo sessions it resolves from fixtures.
    const summary = this.employee.contributionSummary;

    if (this.isDemoMode && (!summary || summary.total === undefined)) {
      // Demo sessions without contribution fixtures (e.g. TAS-only demos get
      // `summary: {}` from the registry catch-all) read the local snapshot —
      // demos must never reach the server. Live users must never take this
      // branch: the snapshot is mock data (hardcoded $8,750 lifetime).
      const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
      return {
        contributions: snapshot.employerContributions,
        isLiveData: false,
      };
    }

    // Calculate year-to-date from the same JSONAPI transaction source
    // that fetchContributionHistory() uses (single source of truth)
    let yearToDate = 0;
    let lifetimeTotal: number;
    let isLiveData = true;
    try {
      const transactionsResult = await this.loadContributionTransactions();
      const payments = normalizeContributionTransactions(transactionsResult.transactions);

      yearToDate = calculateContributionYearToDate(payments);
      lifetimeTotal = calculateContributionLifetimeTotal(payments);
    } catch (error) {
      console.warn('[reimagined-data] Failed to fetch transactions for contribution totals', error);
      lifetimeTotal = (summary?.total || 0) / 100;
      isLiveData = summary?.total !== undefined;
    }

    // Get the plan annual limit to estimate projected annual
    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimit = plan?.annualLimit || 5250;

    // Project based on current benefit-year YTD and time elapsed in the benefit year.
    const monthsElapsed = getBenefitYearMonthsElapsed();
    const projectedAnnual = Math.min(Math.round((yearToDate / monthsElapsed) * 12), annualLimit);

    return {
      contributions: {
        yearToDate,
        lifetime: lifetimeTotal,
        projectedAnnual,
      },
      isLiveData,
    };
  }

  private getSlrAlertTranslationKey(slrAlert: SlrAlert) {
    return slrAlert.type.replace(/-/g, '_');
  }

  private getSlrAlertTitle(slrAlert: SlrAlert) {
    return this.intl.t(`slr.reimagined.alert_${this.getSlrAlertTranslationKey(slrAlert)}_title`, {
      default: slrAlert.title,
    });
  }

  private getSlrAlertDescription(slrAlert: SlrAlert) {
    const isUpload = slrAlert.resolution_type === 'upload';
    const descSuffix =
      slrAlert.type === 'blank-payment-address'
        ? isUpload
          ? 'desc_upload'
          : 'desc_dropdown'
        : 'desc';

    return this.intl.t(
      `slr.reimagined.alert_${this.getSlrAlertTranslationKey(slrAlert)}_${descSuffix}`,
      { default: slrAlert.description }
    );
  }

  private getSlrAlertLabel(slrAlert: SlrAlert) {
    return this.intl.t(`slr.reimagined.alert_${this.getSlrAlertTranslationKey(slrAlert)}_label`, {
      default: this.getSlrAlertTitle(slrAlert),
    });
  }

  private getSlrAlertSuccessMessage(slrAlert: SlrAlert) {
    if (UPDATED_LOAN_INFO_ALERT_TYPES.has(slrAlert.type)) {
      return this.intl.t('slr.reimagined.successfully_updated_loan_information');
    }

    return this.intl.t('slr.reimagined.successfully_added_loan_account');
  }

  private getGroupedUploadTaskDescription(uploadAlerts: SlrAlert[]) {
    const types = new Set(uploadAlerts.map((alert) => alert.type));

    if (types.has('blank-account-number') && types.has('no-loan-balance')) {
      return this.intl.t('slr.reimagined.grouped_upload_task_desc_account_and_balance');
    }

    if (types.has('blank-account-number')) {
      return this.intl.t('slr.reimagined.grouped_upload_task_desc_account_number');
    }

    if (types.has('no-loan-balance')) {
      return this.intl.t('slr.reimagined.grouped_upload_task_desc_loan_balance');
    }

    return this.intl.t('slr.reimagined.grouped_upload_task_desc_missing_info');
  }

  private createActionItemFromSlrAlert(slrAlert: SlrAlert): ContributionActionItem {
    const isUpload = slrAlert.resolution_type === 'upload';
    const isDropdown = slrAlert.resolution_type === 'dropdown';
    const successMessage = isUpload ? this.getSlrAlertSuccessMessage(slrAlert) : undefined;
    const badgeIntent: ContributionActionItem['badgeIntent'] =
      slrAlert.display_mode === 'single' ? 'danger' : 'warning';

    return {
      id: `slr-${slrAlert.type}`,
      title: this.getSlrAlertTitle(slrAlert),
      description: this.getSlrAlertDescription(slrAlert),
      badge:
        isUpload || isDropdown
          ? this.intl.t('slr.reimagined.action_act_now')
          : this.intl.t('slr.reimagined.action_action_needed'),
      badgeIntent,
      route: isDropdown ? 'authenticated.reimagined.loans' : 'authenticated.observability.upload',
      issueType: slrAlert.category,
      resolutionType: slrAlert.resolution_type,
      metadata: slrAlert.metadata,
      ...(successMessage ? { successMessage } : {}),
    };
  }

  private createGroupedActionItem(groupedAlerts: SlrAlert[]): ContributionActionItem {
    const uploadAlerts = groupedAlerts.filter((alert) => alert.resolution_type === 'upload');
    const dropdownAlerts = groupedAlerts.filter((alert) => alert.resolution_type === 'dropdown');
    const tasks: ContributionActionTask[] = [];

    if (uploadAlerts.length) {
      tasks.push({
        id: 'upload-loan-information',
        title: this.intl.t('slr.reimagined.grouped_upload_task_title'),
        description: this.getGroupedUploadTaskDescription(uploadAlerts),
        resolutionType: 'upload',
        issueLabels: uploadAlerts.map((alert) => this.getSlrAlertLabel(alert)),
        successMessage: this.intl.t('slr.reimagined.successfully_updated_loan_information'),
      });
    }

    for (const alert of dropdownAlerts) {
      tasks.push({
        id: alert.type,
        title: this.intl.t('slr.reimagined.grouped_address_task_title'),
        description: this.getSlrAlertDescription(alert),
        resolutionType: 'dropdown',
        issueLabels: [this.getSlrAlertLabel(alert)],
        metadata: alert.metadata,
      });
    }

    const descriptionKey = dropdownAlerts.length
      ? 'slr.reimagined.alert_grouped_account_info_desc_mixed'
      : 'slr.reimagined.alert_grouped_account_info_desc_upload';

    return {
      id: 'slr-grouped-account-info',
      title: this.intl.t('slr.reimagined.alert_grouped_account_info_title'),
      description: this.intl.t(descriptionKey),
      badge: this.intl.t('slr.reimagined.action_act_now'),
      badgeIntent: 'warning',
      route: 'authenticated.reimagined.loans',
      issueType: 'account-setup',
      tasks,
      successMessage: this.intl.t('slr.reimagined.successfully_updated_loan_information'),
    };
  }

  private getSlrActionItems(slrAlerts: SlrAlert[]): ContributionActionItem[] {
    const groupedAlerts = slrAlerts.filter(
      (alert) => alert.display_mode === 'grouped' && GROUPABLE_SLR_ALERT_TYPES.has(alert.type)
    );

    if (groupedAlerts.length <= 1) {
      return slrAlerts.map((alert) => this.createActionItemFromSlrAlert(alert));
    }

    const groupedAlertSet = new Set(groupedAlerts);
    const groupedInsertIndex = slrAlerts.findIndex((alert) => groupedAlertSet.has(alert));
    const actionItems: ContributionActionItem[] = slrAlerts
      .filter((alert) => !groupedAlertSet.has(alert))
      .map((alert) => this.createActionItemFromSlrAlert(alert));

    actionItems.splice(groupedInsertIndex, 0, this.createGroupedActionItem(groupedAlerts));
    return actionItems;
  }

  /**
   * Fetches comprehensive contribution history for the reimagined finance/history page.
   * Combines past payments, future payments, account alerts, and eligibility status
   * into a single unified data structure.
   *
   * Uses JSONAPI queries to `accounting-transactions` (with `accounting-line-item` included),
   * giving us server-side pagination, sorting, and filtering.
   */
  async fetchContributionHistory(): Promise<ContributionHistoryData> {
    const alerts = this.employee.accountAlerts;
    const person = this.sessionContext.person;
    const employee = this.sessionContext.currentEmployee;
    const cadence = employee?.plan?.cadence;
    const isSlrEligible = !!(cadence && cadence !== 'NEVER');

    // Fetch contribution transactions, future payments, SLR alerts, and the
    // plan's tier schedule in parallel
    let transactionsResult;
    let futurePayments;
    // eslint-disable-next-line no-useless-assignment -- null is the intended sentinel
    let slrAlertsResponse: SlrAlertsResponse | null = null;
    // eslint-disable-next-line no-useless-assignment -- null is the intended sentinel
    let scheduledMonthlyAmount: number | null = null;

    try {
      [transactionsResult, futurePayments, slrAlertsResponse, scheduledMonthlyAmount] =
        await Promise.all([
          this.loadContributionTransactions(),
          this._fetchFuturePayments(),
          this._fetchSlrAlerts(),
          this.#fetchScheduledMonthlyAmount(),
        ]);
    } catch (error) {
      console.error(
        '[reimagined-data] Failed to fetch contribution history, falling back to empty state',
        error
      );

      return {
        payments: [],
        lastPayment: null,
        nextPayment: null,
        lifetimeTotal: 0,
        paymentCount: 0,
        yearToDate: 0,
        isSlrEligible: false,
        accountReceivingName: null,
        hasMultipleAccounts: false,
        accountAlerts: [],
        paymentValidationErrors: [],
        actionItems: [],
        paymentsByYear: [],
        scheduledMonthlyAmount: null,
        isLiveData: false,
      };
    }

    const normalizedPast = normalizeContributionTransactions(transactionsResult.transactions);

    // Normalize future payments (still from legacy preview endpoint for now)
    const normalizedFuture = futurePayments.map((payment, index) =>
      normalizeFutureContributionPayment(payment, index)
    );

    // Get payment validation errors from next future payment (preserve keys for categorization)
    const nextFuture = futurePayments[0];
    const rawValidationErrors: { key?: string; message: string }[] =
      nextFuture?.validation_errors?.filter(
        (err: { key?: string; message: string }) =>
          !!err.message && err.key !== 'NO_PLAN_CONTRIBUTION'
      ) || [];
    const validationErrors = rawValidationErrors.map((err) => err.message);

    const yearToDate = calculateContributionYearToDate(normalizedPast);
    const lifetimeTotal = calculateContributionLifetimeTotal(normalizedPast);

    const accountReceiving = person?.accountReceivingContributions;
    const activeAccounts = person?.activeAccounts || [];

    // Compute action items: the server-side /slr-alerts endpoint is the source
    // of truth for account-setup alerts (upload/address detection). The legacy
    // client-side accountAlerts fallback was removed — when the endpoint
    // returns no alerts, we trust that; only payment validation errors are
    // still derived client-side. A FAILED fetch (null, not empty) is a
    // different case: surface a static outage notice so users with genuine
    // account issues aren't shown a silently-empty attention section.
    const actionItems: ContributionActionItem[] = [];

    if (slrAlertsResponse?.alerts?.length) {
      actionItems.push(...this.getSlrActionItems(slrAlertsResponse.alerts));
    } else {
      if (slrAlertsResponse === null) {
        actionItems.push({
          id: 'slr-alerts-unavailable',
          title: this.intl.t('slr.reimagined.action_alerts_unavailable_title'),
          description: this.intl.t('slr.reimagined.action_alerts_unavailable_desc'),
          badge: this.intl.t('slr.reimagined.action_needs_review'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'default',
        });
      }

      // Create action items from payment validation errors, categorized by key.
      // Dedup by issueType among the validation errors THEMSELVES — seeded
      // empty on purpose so the generic outage notice above never suppresses a
      // real validation message that happens to share its 'default' issueType.
      const coveredIssueTypes = new Set<ContributionActionItem['issueType']>();
      for (const err of rawValidationErrors) {
        const issueType = VALIDATION_KEY_TO_ISSUE_TYPE[err.key ?? ''] ?? 'default';
        if (coveredIssueTypes.has(issueType)) continue;
        coveredIssueTypes.add(issueType);

        const descKey =
          issueType === 'plan-requirement'
            ? 'plan_issue'
            : issueType === 'account-ineligible'
              ? 'account_ineligible_desc'
              : 'resolve_error';

        const badgeKey =
          issueType === 'account-ineligible'
            ? 'action_required_for_payment'
            : issueType === 'plan-requirement'
              ? 'action_action_needed'
              : 'action_needs_review';

        actionItems.push({
          id: `validation-${err.key ?? 'unknown'}`,
          title:
            issueType === 'account-ineligible'
              ? this.intl.t('slr.reimagined.action_account_ineligible_title')
              : err.message,
          description: this.intl.t(`slr.reimagined.action_${descKey}`),
          badge: this.intl.t(`slr.reimagined.${badgeKey}`),
          badgeIntent:
            issueType === 'plan-requirement' || issueType === 'account-ineligible'
              ? 'danger'
              : 'warning',
          route: 'authenticated.observability.upload',
          issueType,
        });
      }
    }

    // Contribution account PARTIALLY_APPLIED: the setup account has no servicer match.
    // Only show when the API has not already included this in slrAlerts, and only during
    // account setup (i.e. the user has no other active accounts to fall back on).
    const hasPartiallyAppliedAlert = actionItems.some(
      (item) => item.id === 'slr-partially-applied'
    );
    if (
      !hasPartiallyAppliedAlert &&
      slrAlertsResponse?.contribution_account?.status === 'PARTIALLY_APPLIED' &&
      activeAccounts.length <= 1
    ) {
      actionItems.push({
        id: 'slr-partially-applied',
        title: this.intl.t('slr.reimagined.alert_partially_applied_title'),
        description: this.intl.t('slr.reimagined.alert_partially_applied_desc'),
        badge: this.intl.t('slr.reimagined.action_action_needed'),
        badgeIntent: 'danger',
        route: 'authenticated.reimagined.loans',
        issueType: 'account-setup',
        resolutionType: 'contact-support',
      });
    }

    // Compute payments grouped by year
    const paymentsByYear = this.groupPaymentsByYear(normalizedPast);

    return {
      payments: [...normalizedFuture, ...normalizedPast],
      lastPayment: normalizedPast[0] || null,
      nextPayment: normalizedFuture[0] || null,
      lifetimeTotal,
      // Count from the same filtered set the totals use — the server-side
      // meta.page.total also counts canceled/failed rows, and every page is
      // already fetched so normalizedPast is the complete list.
      paymentCount: normalizedPast.filter(isSuccessfulContributionPayment).length,
      yearToDate,
      isSlrEligible,
      accountReceivingName: accountReceiving?.institutionName || null,
      hasMultipleAccounts: activeAccounts.length > 1,
      accountAlerts: alerts,
      paymentValidationErrors: validationErrors,
      actionItems,
      paymentsByYear,
      scheduledMonthlyAmount,
      isLiveData: normalizedPast.length > 0 || isSlrEligible,
      pageState: normalizeSlrPageState(slrAlertsResponse?.page_state),
      slrAlerts: slrAlertsResponse?.alerts ?? undefined,
    };
  }

  /**
   * Fetches future payments from the legacy preview endpoint.
   * TODO: Move this to a JSONAPI resource when tio-payments data is migrated.
   */
  private async _fetchFuturePayments(): Promise<FutureContributionPayment[]> {
    const employee = this.sessionContext.currentEmployee;
    if (!employee?.plan || employee.plan.cadence === 'NEVER') {
      return [];
    }

    try {
      // WarpDrive builder read (wave 1.5) — replaces the custom employee
      // adapter `getAccountingFuturePayments` method.
      // PLURAL /employees/{id}/accounting_preview; demoMode honoured natively.
      const response = await this.store.request(accountingPreviewRequest(employee.id!));
      return (response.content as FutureContributionPayment[]) || [];
    } catch (error) {
      console.warn('[reimagined-data] Failed to fetch future payments', error);
      return [];
    }
  }

  /**
   * Fetches the plan's configured per-cycle contribution (dollars) from the
   * tier relationship route. The plan record from /me carries no tier data
   * (the v1 serializer dropped the inline tiers attribute in Dec 2025), so
   * this is the only live source for the scheduled amount — used as the
   * "next contribution" display fallback when the preview endpoint fails.
   * Returns null when unavailable; never throws.
   */
  async #fetchScheduledMonthlyAmount(): Promise<number | null> {
    const plan = this.sessionContext.currentEmployee?.plan;
    if (!plan?.id || plan.cadence === 'NEVER') {
      return null;
    }

    // Demo sessions must never reach the server: their plan fixture carries
    // inline tiers that scheduledMonthlyContribution reads directly.
    if (this.isDemoMode) {
      return null;
    }

    try {
      const response = await this.store.request(planTiersRequest(plan.id));
      const doc = response.content as {
        data?: { attributes?: { fromCycle?: number; amount?: number } }[];
      };
      const tiers = [...(doc?.data ?? [])].sort(
        (a, b) => (a.attributes?.fromCycle ?? 0) - (b.attributes?.fromCycle ?? 0)
      );
      const cents = tiers[0]?.attributes?.amount;
      return typeof cents === 'number' && cents > 0 ? cents / 100 : null;
    } catch (error) {
      console.warn('[reimagined-data] Failed to fetch plan tiers', error);
      return null;
    }
  }

  /**
   * Fetches SLR alerts from the API. Returns null when the call FAILS —
   * distinct from a successful response with zero alerts — so the caller can
   * surface a static outage notice instead of treating the failure as
   * "no issues". (The legacy client-side accountAlerts fallback is gone;
   * there is no client-side re-detection on failure.)
   */
  private async _fetchSlrAlerts(): Promise<SlrAlertsResponse | null> {
    const person = this.sessionContext.person;
    // No person = alerts not applicable, NOT an outage — return an empty
    // response (empty page_state normalizes to undefined downstream).
    if (!person?.id) return { page_state: '', alerts: [], contribution_account: null };

    try {
      const { content } = (await this.store.request(getSlrAlertsRequest(person.id))) as {
        content: SlrAlertsResponse;
      };
      return content;
    } catch (error) {
      console.warn('[reimagined-data] Failed to fetch SLR alerts; surfacing outage notice', error);
      return null;
    }
  }

  /**
   * Helper to group payments by year for yearly contribution cards
   */
  private groupPaymentsByYear(payments: ContributionPaymentRecord[]): ContributionYearlyPayment[] {
    const grouped: Record<string, ContributionYearlyPayment> = {};

    payments
      .filter((payment) => !payment.isFuture)
      .forEach((payment) => {
        const year = getPaymentYear(payment);
        if (!year) return;

        if (!grouped[year]) {
          grouped[year] = {
            year,
            total: 0,
            accounts: [],
            paymentCount: 0,
            sentCount: 0,
            returnedCount: 0,
            resentCount: 0,
          };
        }

        const entry = grouped[year]!;

        // Track status-specific counts
        if (payment.status === 'sent') {
          entry.sentCount += 1;
        } else if (payment.status === 'failed') {
          entry.returnedCount += 1;
        } else if (payment.status === 'resent') {
          entry.resentCount += 1;
        }

        // Track by servicer/account for all payments so failed-only years still
        // show their related accounts, but only non-failed payments contribute
        // to totals.
        const servicer = payment.servicer;
        const existingAccount = entry.accounts.find((a) => a.name === servicer);

        // Failed payments don't count toward total or paymentCount
        if (payment.status !== 'failed') {
          entry.total += payment.amount;
          entry.paymentCount += 1;

          if (existingAccount) {
            existingAccount.amount += payment.amount;
          } else {
            entry.accounts.push({ name: servicer, amount: payment.amount });
          }
        } else if (!existingAccount) {
          entry.accounts.push({ name: servicer, amount: 0 });
        }
      });

    // Convert to array and sort by year descending
    return Object.values(grouped).sort((a, b) => parseInt(b.year) - parseInt(a.year));
  }

  /**
   * Determines comprehensive data source info for all reimagined pages.
   * This tracks which data comes from live backend vs demo/placeholder sources.
   */
  async getDataSourceInfo(): Promise<DataSourceInfo> {
    const loansData = await this.fetchLoansData();
    const contributionsData = await this.fetchEmployerContributions();
    const section127Info = await this.fetchSection127Info();
    // Employee profile is available but not used directly in this method
    // It's used in fetchDashboardDataWithLive instead

    const hasEmployee = !!this.sessionContext.currentEmployee?.id;
    const hasPslfProfile = !!this.sessionContext.currentEmployee?.pslfProfile;
    const hasTasParticipant = !!this.sessionContext.currentEmployee?.tasParticipant;

    return {
      // Employee/Profile Data
      employeeProfile: hasEmployee ? 'live' : 'demo',
      employerName: this.sessionContext.company?.name ? 'live' : 'demo',

      // Financial Data
      studentLoans: loansData.hasLinkedLoans ? 'live' : 'demo',
      employerContributions: contributionsData.isLiveData ? 'live' : 'demo',
      section127Allocation: section127Info.isLiveData ? 'live' : 'demo',
      section127Used: section127Info.isLiveData ? 'live' : 'demo',

      // Benefit Eligibility
      pslfEligibility: hasPslfProfile || this.partner.isPslfEnabled ? 'live' : 'demo',
      tasEligibility: hasTasParticipant || this.partner.hasNewTuitionAssistance ? 'live' : 'demo',

      // Career Data - all placeholder until backend services built
      // GAP: Needs career-pathways microservice or API endpoints in tio-api
      careerCapitalScore: 'placeholder',
      skills: 'placeholder',
      careerPlan: 'placeholder',
      recommendations: 'placeholder',

      // Optimizer - placeholder until AI service built
      // GAP: Needs AI recommendation engine to analyze optimal allocation
      optimization: 'placeholder',
    };
  }

  /**
   * Fetches live employee profile data from session context.
   * This data is already loaded as part of the session.
   */
  fetchLiveEmployeeProfile(): LiveEmployeeProfile | null {
    if (this.isDemoMode) {
      return null;
    }

    const employee = this.sessionContext.currentEmployee;
    const person = this.sessionContext.person;
    const plan = employee?.plan;

    if (!employee?.id) {
      return null;
    }

    // Calculate years in role from employment start date
    const startDate = employee.employmentStartDate || employee.createdAt;
    const msPerYear = 1000 * 60 * 60 * 24 * 365;
    const yearsInRole = startDate
      ? Math.round(((Date.now() - new Date(startDate).getTime()) / msPerYear) * 10) / 10
      : 0;

    // Get contribution YTD from employee service
    const contributionPayments = this.employee.contributionPayments || [];
    const currentYear = new Date().getFullYear();
    const ytdPayments = contributionPayments.filter((payment) => {
      const paymentYear = new Date(
        payment.line_item_date || payment.line_item_period
      ).getFullYear();
      return paymentYear === currentYear;
    });
    const slrYtd = ytdPayments.reduce((total, payment) => total + (payment.amount || 0), 0) / 100;
    const slrLifetime = (this.employee.contributionSummary?.total || 0) / 100;

    return {
      id: employee.id,
      firstName: employee.firstName || person?.firstName || '',
      lastName: employee.lastName || person?.lastName || '',
      email: employee.email || person?.primaryEmail?.email || '',
      currentRole: {
        title: employee.title || 'Employee',
        department: employee.department || '',
        startDate: startDate || new Date().toISOString(),
        yearsInRole,
      },
      taBenefit: {
        annualMax: this.partner.hasNewTuitionAssistance ? 5250 : 0,
        used: 0, // GAP: Need to query TAS applications for used amount
        remaining: this.partner.hasNewTuitionAssistance ? 5250 : 0,
        eligiblePrograms: 0, // GAP: Need program catalog query
      },
      slrBenefit: {
        monthlyContribution: plan?.tiers?.[0]?.amount || 0,
        annualMax: plan?.annualLimit || 0,
        yearToDate: slrYtd,
        lifetime: slrLifetime,
      },
      pslfEligible: !!this.partner.isPslfEnabled,
      // GAP: PSLF payment count would come from pslf-form records or external API
      pslfPaymentCount: 0,
    };
  }

  /**
   * Fetches Section 127 allocation info.
   * Uses live data from plan/contributions when available.
   */
  async fetchSection127Info(): Promise<LiveSection127Info> {
    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimit = plan?.annualLimit || 5250;

    // Get YTD SLR contributions
    const contributionsData = await this.fetchEmployerContributions();
    const slrUsed = contributionsData.contributions.yearToDate;

    // Get YTD TAS usage from approved applications
    const tasUsedData = await this.fetchTasUsedAmount();
    const tasUsed = tasUsedData.yearToDate;

    const yearToDateUsed = slrUsed + tasUsed;
    const remaining = Math.max(0, annualLimit - yearToDateUsed);

    // Current allocation is based on what's been used
    const totalUsed = slrUsed + tasUsed || 1;
    const loanAllocation = slrUsed > 0 ? Math.round((slrUsed / totalUsed) * annualLimit) : 0;
    const tuitionAllocation = annualLimit - loanAllocation;

    const hasLiveData = contributionsData.isLiveData || tasUsedData.isLiveData;

    return {
      annualLimit,
      yearToDateUsed,
      remaining,
      loanAllocation: hasLiveData ? loanAllocation : 0,
      tuitionAllocation: hasLiveData ? tuitionAllocation : annualLimit,
      isLiveData: hasLiveData,
    };
  }

  /**
   * Fetches TAS (Tuition Assistance) used amount for current year.
   * Queries approved TAS applications and sums their pre-approved totals.
   *
   * @returns Object with yearToDate amount and whether data is live
   */
  async fetchTasUsedAmount(): Promise<{ yearToDate: number; isLiveData: boolean }> {
    if (this.isDemoMode) {
      return { yearToDate: this.getDemoTasYearToDateUsed(), isLiveData: false };
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return { yearToDate: 0, isLiveData: false };
    }

    try {
      const companyId = employee.company?.id || this.sessionContext.company?.id || null;
      const docs = await this.fetchTasSearchDocuments({
        employeeId: employee.id,
        companyId: companyId || null,
      });
      const { applications } = this.mapSearchResultsToLiveTasData(docs);
      const yearToDate = this.computeTasUsedAmount(applications);

      return { yearToDate, isLiveData: true };
    } catch (error) {
      console.warn('Failed to fetch TAS used amount:', error);
      return { yearToDate: 0, isLiveData: false };
    }
  }

  private getDemoTasYearToDateUsed(): number {
    const demoScenario = getCurrentDemoScenario();
    if (!demoScenario.features.tuitionAssistance) {
      return 0;
    }

    const userState = getDemoUserState();
    if (userState.benefitsMaxedOut) {
      return DEMO_TAS_ANNUAL_LIMIT;
    }

    const tasScenario = getCurrentDemoTasScenarioId();
    if (tasScenario === 'completed') {
      return 3000;
    }

    if (tasScenario === 'in-progress') {
      return 750;
    }

    return 0;
  }

  getDemoTasLimitsStatus(): LimitsStatusDataSignature {
    const annualLimit = DEMO_TAS_ANNUAL_LIMIT * 100;
    const annualUsage = this.getDemoTasYearToDateUsed() * 100;
    const available = Math.max(0, annualLimit - annualUsage);
    const currentYear = new Date().getFullYear();
    const detail = {
      annual_limit: annualLimit,
      annual_usage: annualUsage,
      available,
      balance_multiplier: 1,
      hard: true,
      limit: annualLimit,
      one_month_limit: null,
      one_month_usage: 0,
      six_month_limit: null,
      six_month_usage: annualUsage,
      taxable_limit: null,
      three_month_limit: null,
      three_month_usage: annualUsage,
      usage: annualUsage,
      external_annual_usage: 0,
    };

    return {
      annual_start_date: `${currentYear}-01-01`,
      annual_end_date: `${currentYear}-12-31`,
      as_of_date: new Date().toISOString().slice(0, 10),
      available,
      available_with_override: available,
      details: {
        company: detail,
        product: detail,
        batch: detail,
        scheme: detail,
      },
      employee_id: 'demo-employee',
      hard_limit: 'company_annual',
      limit_capacity: 'TAS.LimitCapacity.MONETARY',
      product: null,
      scheme: null,
      soft_limit: 'none',
      external_annual_usage: 0,
    };
  }

  /**
   * Fetches comprehensive dashboard data with live employee info where available.
   * Falls back to demo data for career-specific fields.
   */
  async fetchDashboardDataWithLive() {
    const demoData = await this.careerPathwaysApi.fetchDashboardData();
    const liveProfile = this.fetchLiveEmployeeProfile();

    if (!liveProfile || this.isDemoMode) {
      return {
        ...demoData,
        isLiveData: false,
      };
    }

    // Merge live employee data with demo career data
    return {
      ...demoData,
      employee: {
        ...demoData.employee,
        id: liveProfile.id,
        firstName: liveProfile.firstName,
        lastName: liveProfile.lastName,
        email: liveProfile.email,
        currentRole: liveProfile.currentRole,
        taBenefit: liveProfile.taBenefit,
      },
      isLiveData: true,
    };
  }

  /**
   * Fetches live PSLF data from pslf-profile and pslf-form models.
   * Returns PSLF forms, linked accounts/loans data, and estimator information.
   */
  async fetchPslfData(): Promise<LivePslfData> {
    if (this.isDemoMode && getCurrentDemoPslfScenarioId() !== 'live-data') {
      return this.getDemoPslfData();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return this.getEmptyPslfData();
    }

    try {
      // Check if PSLF is enabled for the company
      const isPslfEnabled = this.partner.isPslfEnabled;
      if (!isPslfEnabled) {
        return this.getEmptyPslfData();
      }

      // Fetch PSLF profile with forms
      const pslfProfile = employee.pslfProfile as PslfProfileModel | null;

      if (!pslfProfile) {
        // User eligible but hasn't enrolled
        return this.getEmptyPslfData();
      }

      // Fetch all PSLF forms for this profile
      // WarpDrive builder read (Phase 2.4) — replaces store.query;
      // flattened bracket keys satisfy the builder's QueryParamsSource type.
      const { content: pslfFormsContent } = (await this.store.request(
        query<PslfFormModel>('pslf-form', {
          'filter[pslf-profile]': pslfProfile.id as string,
          include: 'attachments',
        })
      )) as { content: { data: PslfFormModel[] } };
      const forms = pslfFormsContent.data;

      // Filter out forms with null IDs (shouldn't happen, but defensive)
      const processedForms: PslfFormModel[] = forms.filter((form) => form.id !== null);

      // Get current employer from most recent form
      const currentForm = processedForms.find((f) => f.formType === 'currentEmployer');
      const currentEmployer = currentForm
        ? {
            name: currentForm.employerDisplayName || 'Unknown Employer',
            isQualifying: true,
            startDate: currentForm.employmentStartDate,
          }
        : null;

      // Fetch accounts/loans data (same pattern as qualifying-payment-tracker)
      const loansData = await this.fetchLoansData();
      const accounts = accountsToLiveAccounts(loansData.activeAccounts ?? []);

      return {
        currentEmployer,
        forms: processedForms,
        accounts,
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: true,
      };
    } catch (error) {
      console.warn('Failed to fetch PSLF data:', error);
      return this.getEmptyPslfData();
    }
  }

  private getEmptyPslfData(): LivePslfData {
    return {
      currentEmployer: null,
      forms: [],
      accounts: [],
      hasCompletedEstimator: false,
      estimatorResults: null,
      isLiveData: true,
    };
  }

  private getDemoPslfData(scenarioOverride?: string): LivePslfData {
    const scenario = scenarioOverride ?? getCurrentDemoPslfScenarioId();

    // Helper: build a date string N days ago (YYYY-MM-DD)
    const daysAgo = (n: number) => {
      const d = new Date();
      d.setUTCDate(d.getUTCDate() - n);
      return d.toISOString().slice(0, 10);
    };

    // Helper: push an attributes payload into the store and return the record.
    // Using `store.push` (not `createRecord`) marks the record as already
    // saved/clean, so an accidental `.save()` call is a no-op rather than
    // POSTing demo data to the backend.
    const pushForm = (id: string, attrs: Record<string, unknown>): PslfFormModel => {
      this.store.push({
        data: {
          type: 'pslf-form',
          id,
          attributes: attrs,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return this.store.peekRecord('pslf-form', id) as PslfFormModel;
    };

    // Shared form attribute templates (no id — id is assigned at push time
    // so scenario variants can override fields without colliding).
    const currentEmployerAttrs = {
      status: 'submitted',
      formType: 'currentEmployer',
      borrowerCompletedAt: daysAgo(17),
      employerCompletedAt: daysAgo(13),
      submittedAt: daysAgo(12),
      createdAt: daysAgo(20),
      borrowerFields: {
        'borrower-name': 'Jane Doe',
        'borrower-birth-date': '1990-05-12',
        'borrower-email': 'jane.doe@example.com',
        'borrower-primary-phone': '555-123-4567',
        'borrower-alternate-phone': null,
        'borrower-address': '123 Main St',
        'borrower-apartment': null,
        'borrower-city': 'Portland',
        'borrower-state': 'OR',
        'borrower-zip': '97201',
        'borrower-employment-start-date': '2022-01-15',
        'borrower-employment-end-date': null,
        'borrower-employment-hours': 'full-time',
        'borrower-employment-status': 'employed',
        'borrower-employment-continues': true,
        'borrower-employer-name': 'Acme Healthcare',
        'borrower-employer-ein': '12-3456789',
        'borrower-employer-email': 'hr@acmehealthcare.com',
        'borrower-employer-website': 'https://acmehealthcare.com',
        'borrower-employer-contact-name': 'Sarah Johnson',
        'borrower-employer-contact-title': 'HR Director',
        'borrower-employer-contact-phone-number': '555-987-6543',
        'borrower-non-partner-form': false,
        'borrower-prior-employer-closed': false,
        'borrower-wants-forbearance': false,
        'borrower-print-and-mail': false,
        'borrower-sign-date': daysAgo(17),
        'borrower-reason': null,
      },
      employerFields: {
        'employer-name': 'Acme Healthcare',
        'employer-fein': '12-3456789',
        'employer-email': 'hr@acmehealthcare.com',
        'employer-website': 'https://acmehealthcare.com',
        'employer-street-address': '456 Hospital Blvd',
        'employer-apartment': 'Suite 200',
        'employer-city': 'Portland',
        'employer-state': 'OR',
        'employer-zip': '97205',
        'employer-contact-name': 'Sarah Johnson',
        'employer-contact-title': 'HR Director',
        'employer-contact-phone-number': '555-987-6543',
        'employer-contact-email': 'sarah.johnson@acmehealthcare.com',
        'employment-start-date': '2022-01-15',
        'employment-end-date': null,
        'employment-hours': 'full-time',
        'employment-status': 'employed',
        'employment-continues': true,
        'employer-sign-date': daysAgo(13),
      },
    };

    const priorEmployerSubmittedAttrs = {
      status: 'submitted',
      formType: 'priorEmployer',
      borrowerCompletedAt: daysAgo(30),
      employerCompletedAt: daysAgo(25),
      submittedAt: daysAgo(24),
      createdAt: daysAgo(32),
      borrowerFields: {
        'borrower-name': 'Jane Doe',
        'borrower-birth-date': '1990-05-12',
        'borrower-email': 'jane.doe@example.com',
        'borrower-primary-phone': '555-123-4567',
        'borrower-alternate-phone': null,
        'borrower-address': '123 Main St',
        'borrower-apartment': null,
        'borrower-city': 'Portland',
        'borrower-state': 'OR',
        'borrower-zip': '97201',
        'borrower-employment-start-date': '2019-06-01',
        'borrower-employment-end-date': '2021-12-31',
        'borrower-employment-hours': 'full-time',
        'borrower-employment-status': 'employed',
        'borrower-employment-continues': false,
        'borrower-employer-name': 'City General Hospital',
        'borrower-employer-ein': '98-7654321',
        'borrower-employer-email': 'hr@citygeneralhospital.org',
        'borrower-employer-website': 'https://citygeneralhospital.org',
        'borrower-employer-contact-name': 'Mike Chen',
        'borrower-employer-contact-title': 'Payroll Manager',
        'borrower-employer-contact-phone-number': '555-222-3333',
        'borrower-non-partner-form': false,
        'borrower-prior-employer-closed': false,
        'borrower-wants-forbearance': false,
        'borrower-print-and-mail': false,
        'borrower-sign-date': daysAgo(30),
        'borrower-reason': null,
      },
      employerFields: {
        'employer-name': 'City General Hospital',
        'employer-fein': '98-7654321',
        'employer-email': 'hr@citygeneralhospital.org',
        'employer-website': 'https://citygeneralhospital.org',
        'employer-street-address': '789 Medical Center Dr',
        'employer-apartment': null,
        'employer-city': 'Portland',
        'employer-state': 'OR',
        'employer-zip': '97210',
        'employer-contact-name': 'Mike Chen',
        'employer-contact-title': 'Payroll Manager',
        'employer-contact-phone-number': '555-222-3333',
        'employer-contact-email': 'mike.chen@citygeneralhospital.org',
        'employment-start-date': '2019-06-01',
        'employment-end-date': '2021-12-31',
        'employment-hours': 'full-time',
        'employment-status': 'employed',
        'employment-continues': false,
        'employer-sign-date': daysAgo(25),
      },
    };

    const priorEmployerPendingAttrs = {
      status: 'borrower_signed',
      formType: 'priorEmployer',
      borrowerCompletedAt: daysAgo(7),
      employerCompletedAt: null,
      submittedAt: null,
      createdAt: daysAgo(9),
      borrowerFields: {
        'borrower-name': 'Jane Doe',
        'borrower-birth-date': '1990-05-12',
        'borrower-email': 'jane.doe@example.com',
        'borrower-primary-phone': '555-123-4567',
        'borrower-alternate-phone': null,
        'borrower-address': '123 Main St',
        'borrower-apartment': null,
        'borrower-city': 'Portland',
        'borrower-state': 'OR',
        'borrower-zip': '97201',
        'borrower-employment-start-date': '2017-08-15',
        'borrower-employment-end-date': '2019-05-31',
        'borrower-employment-hours': 'full-time',
        'borrower-employment-status': 'employed',
        'borrower-employment-continues': false,
        'borrower-employer-name': 'Riverside Community College',
        'borrower-employer-ein': '55-1234567',
        'borrower-employer-email': 'hr@riverside.edu',
        'borrower-employer-website': 'https://riverside.edu',
        'borrower-employer-contact-name': 'Lisa Park',
        'borrower-employer-contact-title': 'HR Coordinator',
        'borrower-employer-contact-phone-number': '555-444-5555',
        'borrower-non-partner-form': false,
        'borrower-prior-employer-closed': false,
        'borrower-wants-forbearance': false,
        'borrower-print-and-mail': false,
        'borrower-sign-date': daysAgo(7),
        'borrower-reason': null,
      },
      employerFields: {},
    };

    // Mock accounts/loans data (mirrors what qualifying-payment-tracker uses)
    const demoAccounts: LiveAccount[] = [
      {
        id: 'demo-acct-1',
        institutionName: 'MOHELA',
        loans: [
          {
            id: 'demo-loan-1',
            name: 'Direct Subsidized Loan',
            balance: 28500,
            interestRate: 4.53,
            qualifyingPayments: 60,
            repaymentPlan: 'SAVE',
            isPaidOff: false,
            status: 'In Repayment',
          },
          {
            id: 'demo-loan-2',
            name: 'Direct Unsubsidized Loan',
            balance: 34200,
            interestRate: 6.08,
            qualifyingPayments: 60,
            repaymentPlan: 'SAVE',
            isPaidOff: false,
            status: 'In Repayment',
          },
        ],
      },
      {
        id: 'demo-acct-2',
        institutionName: 'Aidvantage',
        loans: [
          {
            id: 'demo-loan-3',
            name: 'Direct PLUS Loan',
            balance: 24800,
            interestRate: 7.08,
            qualifyingPayments: 48,
            repaymentPlan: 'SAVE',
            isPaidOff: false,
            status: 'In Repayment',
          },
        ],
      },
    ];

    const estimatorResults = {
      estimatedMonthlyPayment: 285,
      estimatedForgivenessAmount: 62340,
      estimatedForgivenessDate: '2031-06-15',
      repaymentPlan: 'SAVE',
    };

    const scenarioData: Record<string, LivePslfData> = {
      'blank-slate': {
        currentEmployer: null,
        forms: [],
        accounts: [],
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: false,
      },

      'borrower-signed': {
        currentEmployer: {
          name: 'Acme Healthcare',
          isQualifying: true,
          startDate: '2022-01-15',
        },
        forms: [
          pushForm('demo-ecf-bs-1', {
            ...currentEmployerAttrs,
            status: 'borrower_signed',
            borrowerCompletedAt: '2026-04-01',
            employerCompletedAt: null,
            submittedAt: null,
            createdAt: '2026-03-28',
            employerFields: {},
          }),
        ],
        accounts: [],
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: false,
      },

      'employer-signed': {
        currentEmployer: {
          name: 'Acme Healthcare',
          isQualifying: true,
          startDate: '2022-01-15',
        },
        forms: [
          pushForm('demo-ecf-es-1', {
            ...currentEmployerAttrs,
            status: 'awaiting_submit',
            borrowerCompletedAt: '2026-03-25',
            employerCompletedAt: '2026-03-28',
            submittedAt: null,
            createdAt: '2026-03-22',
          }),
        ],
        accounts: [],
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: false,
      },

      submitted: {
        currentEmployer: {
          name: 'Acme Healthcare',
          isQualifying: true,
          startDate: '2022-01-15',
        },
        forms: [pushForm('demo-ecf-1', currentEmployerAttrs)],
        accounts: [],
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: false,
      },

      'form-rejected': {
        currentEmployer: {
          name: 'Acme Healthcare',
          isQualifying: true,
          startDate: '2022-01-15',
        },
        forms: [
          pushForm('demo-ecf-rej-1', {
            ...currentEmployerAttrs,
            status: 'rejected',
            submittedAt: null,
            employerRejectedAt: daysAgo(2),
          }),
        ],
        accounts: [],
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: false,
      },

      'multi-form': {
        currentEmployer: {
          name: 'Acme Healthcare',
          isQualifying: true,
          startDate: '2022-01-15',
        },
        forms: [
          pushForm('demo-ecf-1', currentEmployerAttrs),
          pushForm('demo-ecf-2', priorEmployerSubmittedAttrs),
          pushForm('demo-ecf-3', priorEmployerPendingAttrs),
        ],
        accounts: [],
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: false,
      },

      'with-nslds': {
        currentEmployer: {
          name: 'Acme Healthcare',
          isQualifying: true,
          startDate: '2022-01-15',
        },
        forms: [
          pushForm('demo-ecf-1', currentEmployerAttrs),
          pushForm('demo-ecf-2', priorEmployerSubmittedAttrs),
          pushForm('demo-ecf-3', priorEmployerPendingAttrs),
        ],
        accounts: demoAccounts,
        hasCompletedEstimator: false,
        estimatorResults: null,
        isLiveData: false,
      },

      'full-journey': {
        currentEmployer: {
          name: 'Acme Healthcare',
          isQualifying: true,
          startDate: '2022-01-15',
        },
        forms: [
          pushForm('demo-ecf-1', currentEmployerAttrs),
          pushForm('demo-ecf-2', priorEmployerSubmittedAttrs),
          pushForm('demo-ecf-3', priorEmployerPendingAttrs),
        ],
        accounts: demoAccounts,
        hasCompletedEstimator: true,
        estimatorResults,
        isLiveData: false,
      },
    };

    const data = scenarioData[scenario];
    if (data) {
      return data;
    }

    // Fallback for unknown scenario (including 'live-data' which is handled in fetchPslfData)
    return this.getEmptyPslfData();
  }

  /**
   * Fetches live TAS data including eligibility, programs, and applications.
   */
  async fetchTasData(): Promise<LiveTasData> {
    if (this.isDemoMode) {
      return this.getDemoTasData();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return this.getEmptyTasData();
    }

    let participantState: string | null = null;

    try {
      if (this.isTasDebugEnabled) {
        console.info('[reimagined-tas] fetchTasData:start', {
          employeeId: employee.id,
          hasNewTuitionAssistance: this.partner.hasNewTuitionAssistance,
        });
      }

      // Check if TAS is enabled
      const hasTas = this.partner.hasNewTuitionAssistance;
      if (!hasTas) {
        if (this.isTasDebugEnabled) {
          console.info('[reimagined-tas] fetchTasData:disabled');
        }
        return this.getEmptyTasData();
      }

      // Get TAS participant (reload if not already present in session context)
      let participant = employee.tasParticipant;
      let refreshedEmployee: EmployeeModel | null = null;
      if (!participant) {
        // WarpDrive builder read (Phase 2.4) — replaces store.findRecord.
        const { content: empContent1 } = (await this.store.request(
          findRecord<EmployeeModel>('employee', employee.id as string, {
            include: 'tas-participant,company',
            reload: true,
          })
        )) as { content: { data: EmployeeModel } };
        refreshedEmployee = empContent1.data;
        participant = refreshedEmployee?.tasParticipant;
      }
      if (this.isTasDebugEnabled) {
        console.info('[reimagined-tas] fetchTasData:participant', {
          participantId: participant?.id ?? null,
        });
      }
      participantState = participant?.state ?? null;
      const companyId =
        employee.company?.id ||
        refreshedEmployee?.company?.id ||
        this.sessionContext.company?.id ||
        null;

      const docs = await this.fetchTasSearchDocuments({
        employeeId: employee.id,
        companyId,
      });
      const { applications, programs, hasApplicationIds, hasApplicationDocs } =
        this.mapSearchResultsToLiveTasData(docs);
      // Only fallback to JSONAPI if we have application-type docs that are missing IDs
      // (data quality issue). Instance-only results are valid — the employee may have
      // Draft program instances with no applications yet.
      if (hasApplicationDocs && !hasApplicationIds) {
        if (this.isTasDebugEnabled) {
          console.info('[reimagined-tas] fetchTasData:missing-application-ids', {
            sampleDoc: docs[0] || null,
            totalDocs: docs.length,
          });
        }
        return await this.fetchTasDataFromJsonApi(employee, participantState);
      }
      const summary = this.computeTasSummary(applications);

      return {
        isEligible: true,
        participantState,
        programs,
        applications,
        summary,
        isLiveData: true,
      };
    } catch (error) {
      console.warn('Failed to fetch TAS data:', error);
      return this.fetchTasDataFromJsonApi(employee, participantState);
    }
  }

  /**
   * Fetches live TAS data filtered to only dependent programs.
   * Filters using must_not to exclude records where dependent_id is an empty string.
   */
  async fetchDependentTasData(): Promise<LiveTasData> {
    if (this.isDemoMode) {
      return this.getEmptyDemoTasData();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return this.getEmptyTasData();
    }

    // eslint-disable-next-line no-useless-assignment -- null is the intended sentinel
    let participantState: string | null = null;

    try {
      const hasTas = this.partner.hasNewTuitionAssistance;
      if (!hasTas) {
        return this.getEmptyTasData();
      }

      let participant = employee.tasParticipant;
      let refreshedEmployee: EmployeeModel | null = null;
      if (!participant) {
        // WarpDrive builder read (Phase 2.4) — replaces store.findRecord.
        const { content: empContent2 } = (await this.store.request(
          findRecord<EmployeeModel>('employee', employee.id as string, {
            include: 'tas-participant,company',
            reload: true,
          })
        )) as { content: { data: EmployeeModel } };
        refreshedEmployee = empContent2.data;
        participant = refreshedEmployee?.tasParticipant;
      }
      participantState = participant?.state ?? null;
      const companyId =
        employee.company?.id ||
        refreshedEmployee?.company?.id ||
        this.sessionContext.company?.id ||
        null;

      const docs = await this.fetchTasSearchDocuments({
        employeeId: employee.id,
        companyId,
        dependentOnly: true,
      });
      const { applications, programs } = this.mapSearchResultsToLiveTasData(docs);
      const summary = this.computeTasSummary(applications);

      return {
        isEligible: true,
        participantState,
        programs,
        applications,
        summary,
        isLiveData: true,
      };
    } catch (error) {
      console.warn('Failed to fetch dependent TAS data:', error);
      return this.getEmptyTasData();
    }
  }

  private async fetchTasDataFromJsonApi(employee: EmployeeModel, participantState: string | null) {
    const employeeId = employee.id;
    if (!employeeId) {
      return this.getEmptyTasData();
    }

    // WarpDrive builder read (Phase 2.4) — replaces store.query;
    // flattened bracket keys satisfy the builder's QueryParamsSource type.
    const { content: appsContent } = (await this.store.request(
      query<TasApplicationModel>('tas-application', {
        'filter[employee]': employeeId,
        include: 'tas-program-instance.tas-program-template,tas-courses',
      })
    )) as { content: { data: TasApplicationModel[] } };
    const applications = appsContent.data;

    const processedApps: LiveTasApplication[] = applications
      .filter((app) => app.id !== null)
      .map((app) => ({
        id: app.id as string,
        recordType: 'application' as const,
        programInstanceId: app.tasProgramInstance?.id ?? null,
        name: app.displayName || 'Application',
        state: app.state,
        paymentState: app.paymentState || 'TAS.ApplicationPaymentState.NONE',
        approvedTotal: (app.approvedTotal || 0) / 100,
        requestedTotal: (app.amounts?.requested || 0) / 100,
        paidTotal: (app.amounts?.paid || 0) / 100,
        courseCashPreApprovedTotal: app.fields?.CARD_PRE_APPROVED_TOTAL?.values?.[0] ?? null,
        courseCashFinalApprovedTotal: app.fields?.CARD_FINAL_APPROVED_TOTAL?.values?.[0] ?? null,
        courseCashUsed:
          app.amounts?.course_cash?.used === undefined ? null : app.amounts.course_cash.used / 100,
        courseCashReserved:
          app.amounts?.course_cash?.reserved === undefined
            ? null
            : app.amounts.course_cash.reserved / 100,
        coursesBeginDate: app.coursesBeginDate || null,
        coursesEndDate: app.fields?.COURSES_END_DATE?.values?.[0] || null,
        createdAt: app.createdAt,
        updatedAt: app.updatedAt || null,
        programName: app.tasProgramInstance?.tasProgramTemplate?.programName || null,
        programTemplateId: app.tasProgramInstance?.tasProgramTemplate?.id || null,
        dependentId: null,
        dependentName: null,
      }));

    // WarpDrive builder read (Phase 2.4) — replaces store.query;
    // flattened bracket keys satisfy the builder's QueryParamsSource type.
    const { content: piContent } = (await this.store.request(
      query<TasProgramInstanceModel>('tas-program-instance', {
        'filter[employee]': employeeId,
        include: 'tas-program-template',
      })
    )) as { content: { data: TasProgramInstanceModel[] } };
    const programInstances = piContent.data;

    const processedPrograms: LiveTasProgram[] = programInstances
      .filter((pi) => pi.id !== null)
      .map((pi) => ({
        id: pi.id as string,
        name: pi.tasProgramTemplate?.programName || 'Program',
        state: pi.state,
        institution: pi.fields?.SCHOOL_INSTITUTION_NAME?.values?.[0] || null,
        major: pi.fields?.PROGRAM_MAJOR?.values?.[0] || null,
        startDate: pi.fields?.ESTIMATED_PROGRAM_BEGIN?.values?.[0] || null,
        updatedAt: pi.updatedAt || null,
        typeClassification: pi.tasProgramTemplate?.typeClassification || null,
        dependentId: null,
        dependentName: null,
      }));

    return {
      isEligible: true,
      participantState,
      programs: processedPrograms,
      applications: processedApps,
      summary: this.computeTasSummary(processedApps),
      isLiveData: true,
    };
  }

  private computeTasSummary(applications: LiveTasApplication[]) {
    const applicationRecords = applications.filter((app) => app.recordType !== 'instance');
    const approvedStates = new Set([
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.FULFILLED',
    ]);

    const pendingStates = new Set([
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    ]);

    const activeStates = new Set([
      'TAS.ApplicationState.ATTEND',
      'TAS.ApplicationState.COURSES_APPROVED',
    ]);

    return {
      totalApproved: applicationRecords
        .filter((app) => approvedStates.has(app.state))
        .reduce((sum, app) => sum + app.approvedTotal, 0),
      totalPaid: applicationRecords.reduce((sum, app) => sum + app.paidTotal, 0),
      pendingCount: applicationRecords.filter((app) => pendingStates.has(app.state)).length,
      activeCount: applicationRecords.filter((app) => activeStates.has(app.state)).length,
      completedCount: applicationRecords.filter(
        (app) => app.state === 'TAS.ApplicationState.FULFILLED'
      ).length,
    };
  }

  private computeTasUsedAmount(applications: LiveTasApplication[]) {
    const applicationRecords = applications.filter((app) => app.recordType !== 'instance');
    const currentYear = new Date().getFullYear();
    const approvedStates = new Set([
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.FULFILLED',
    ]);

    return applicationRecords
      .filter((app) => approvedStates.has(app.state))
      .filter((app) => {
        const appDate = new Date(app.createdAt);
        return !Number.isNaN(appDate.getTime()) && appDate.getFullYear() === currentYear;
      })
      .reduce((sum, app) => sum + app.approvedTotal, 0);
  }

  private mapSearchResultsToLiveTasData(docs: RecentApplication[]) {
    const applicationDocs = docs.filter(
      (app) => app.recordType === 'application' || app.tasApplicationIdKeyword
    );
    const instanceDocs = docs.filter((app) => app.recordType === 'instance');
    const programsById = new Map<string, LiveTasProgram>();
    const hasApplicationIds = applicationDocs.some((app) => !!app.tasApplicationIdKeyword);

    for (const app of docs) {
      const programId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId;
      if (!programId) {
        continue;
      }

      const existing = programsById.get(programId);
      const programName = app.tasProgramTemplateFieldProgramNameKeyword || null;
      const institution = app.tasProgramInstanceFieldSchoolInstitutionNameKeyword || null;
      const major =
        app.tasProgramInstanceFieldProgramMajorKeyword ||
        app.programMajor ||
        app.tasProgramInstanceFieldProgramSpecializationKeyword ||
        app.programSpecialization ||
        null;
      const startDate = app.tasProgramInstanceCreatedAtDate || app.createdAt || null;
      const updatedAt = app.updatedAt || null;
      const typeClassification = app.tasProgramTemplateTypeKeyword || null;
      const state = app.tasProgramInstanceStateKeyword || 'TAS.ProgramInstanceState.CREATED';

      const dependentId = app.tasProgramInstanceDependentIdKeyword || null;
      const dependentName = app.tasProgramInstanceDependentNameKeyword || null;

      if (!existing) {
        programsById.set(programId, {
          id: programId,
          name: programName || 'Program',
          state,
          institution,
          major,
          startDate,
          updatedAt,
          typeClassification,
          dependentId,
          dependentName,
        });
      } else {
        programsById.set(programId, {
          ...existing,
          name: existing.name || programName || 'Program',
          state: existing.state || state,
          institution: existing.institution || institution,
          major: existing.major || major,
          startDate: existing.startDate || startDate,
          updatedAt: existing.updatedAt || updatedAt,
          typeClassification: existing.typeClassification || typeClassification,
          dependentId: existing.dependentId || dependentId,
          dependentName: existing.dependentName || dependentName,
        });
      }
    }

    const applications = [...applicationDocs, ...instanceDocs].map((app) => {
      const recordType: 'application' | 'instance' =
        (app.recordType as 'application' | 'instance') ??
        (app.tasApplicationIdKeyword ? 'application' : 'instance');
      const programInstanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || null;
      const applicationId =
        recordType === 'application'
          ? app.tasApplicationIdKeyword || app.id
          : programInstanceId || app.id;
      const approvedTotalCents =
        app.paymentFinalApprovedTotalAmount ?? app.paymentPreApprovedTotal ?? 0;

      const createdAt =
        app.tasApplicationCreatedAtDate ||
        app.createdAt ||
        app.documentCreatedAt ||
        app.updatedAt ||
        new Date().toISOString();

      return {
        id: applicationId || programInstanceId || app.id || '',
        recordType,
        programInstanceId,
        name:
          app.tasApplicationFieldApplicationNameKeyword ||
          app.tasProgramInstanceFieldProgramSpecializationKeyword ||
          'Application',
        state: this.getSearchApplicationState(app),
        paymentState: this.derivePaymentState(app),
        approvedTotal: approvedTotalCents / 100,
        requestedTotal:
          (app.tasApplicationFieldPreApprovedTotalInteger ?? app.paymentPreApprovedTotal ?? 0) /
          100,
        paidTotal: (app.paymentPaidAmount || 0) / 100,
        courseCashPreApprovedTotal:
          app.tasApplicationFieldCardPreApprovedTotalInteger === undefined
            ? null
            : app.tasApplicationFieldCardPreApprovedTotalInteger / 100,
        courseCashFinalApprovedTotal:
          app.tasApplicationFieldCardFinalApprovedTotalInteger === undefined
            ? null
            : app.tasApplicationFieldCardFinalApprovedTotalInteger / 100,
        courseCashUsed:
          app.courseCashUsedAmount === undefined ? null : app.courseCashUsedAmount / 100,
        courseCashReserved:
          app.courseCashReservedAmount === undefined ? null : app.courseCashReservedAmount / 100,
        coursesBeginDate: app.tasApplicationFieldCoursesBeginDateDate || null,
        coursesEndDate: app.tasApplicationFieldCoursesEndDateDate || null,
        createdAt,
        updatedAt: app.updatedAt || createdAt,
        programName: app.tasProgramTemplateFieldProgramNameKeyword || null,
        programTemplateId: app.tasProgramTemplateIdKeyword || null,
        dependentId: app.tasProgramInstanceDependentIdKeyword || null,
        dependentName: app.tasProgramInstanceDependentNameKeyword || null,
      };
    });

    return {
      programs: [...programsById.values()],
      applications,
      hasApplicationIds,
      hasApplicationDocs: applicationDocs.length > 0,
    };
  }

  private derivePaymentState(app: RecentApplication) {
    if (isInRepayment(app)) {
      return 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
    }

    if (app.paymentPaidAmount) {
      if (
        app.paymentFinalApprovedTotalAmount &&
        app.paymentPaidAmount >= app.paymentFinalApprovedTotalAmount
      ) {
        return 'TAS.ApplicationPaymentState.PAID_IN_FULL';
      }

      return 'TAS.ApplicationPaymentState.PAID_IN_PART';
    }

    return 'TAS.ApplicationPaymentState.NONE';
  }

  private getSearchApplicationState(app: RecentApplication) {
    if (app.tasApplicationStateKeyword) {
      return app.tasApplicationStateKeyword;
    }

    if (app.recordType === 'instance') {
      return isActiveInstanceState(app.tasProgramInstanceStateKeyword)
        ? 'TAS.ApplicationState.ATTEND'
        : 'TAS.ApplicationState.ABANDONED';
    }

    return 'TAS.ApplicationState.CREATED';
  }

  private async fetchTasSearchDocuments({
    employeeId,
    companyId,
    dependentOnly = false,
  }: {
    employeeId: string;
    companyId: string | null;
    dependentOnly?: boolean;
  }) {
    const now = Date.now();
    const cache = dependentOnly ? this.tasDependentSearchCache : this.tasSearchCache;
    if (
      cache &&
      cache.employeeId === employeeId &&
      cache.companyId === companyId &&
      now - cache.fetchedAt < this.tasSearchCacheTtlMs
    ) {
      return cache.docs;
    }

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const builder = new OpenSearchQueryBuilder().mustTerm('employee_id', employeeId);
    if (companyId) {
      builder.mustTerm('company_id', companyId);
    }

    // Filter to only dependent programs when requested
    // Note: nil dependent_id is stored as "" via Ruby .to_s, so we must exclude empty string
    if (dependentOnly) {
      builder.addFilter({
        bool: {
          must_not: [{ term: { tas_program_instance_dependent_id_keyword: '' } }],
        },
      });
    }

    // Logic: Show applications/instances if they are:
    // 1. Active (regardless of date), OR
    // 2. Inactive/closed but updated within the last month
    builder
      .addFilter({
        bool: {
          should: [
            // Active applications (always show)
            { terms: { tas_application_state_keyword: ACTIVE_APPLICATION_STATES } },
            // Active program instances (always show)
            { terms: { tas_program_instance_state_keyword: ACTIVE_INSTANCE_STATES } },
            // Inactive applications updated recently
            {
              bool: {
                must: [
                  { terms: { tas_application_state_keyword: INACTIVE_APPLICATION_STATES } },
                  { range: { updated_at: { gte: oneMonthAgo.toISOString() } } },
                ],
              },
            },
            // Inactive program instances updated recently
            {
              bool: {
                must: [
                  { terms: { tas_program_instance_state_keyword: INACTIVE_INSTANCE_STATES } },
                  { range: { updated_at: { gte: oneMonthAgo.toISOString() } } },
                ],
              },
            },
          ],
          minimum_should_match: 1,
        },
      })
      .setSort('updated_at:desc')
      .setPagination(1, 200);

    const query = {
      'model-type': 'TasProgramInstance',
      filter: { 'tas-applications.with-deleted': false },
      searchBody: builder.build(),
    };

    const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
      content: OpenSearchResponse<RecentApplication>;
    };

    const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
    const docs = normalizePaymentFields(responseBuilder.getDocuments());

    const cacheEntry = {
      employeeId,
      companyId,
      fetchedAt: now,
      docs,
    };

    if (dependentOnly) {
      this.tasDependentSearchCache = cacheEntry;
    } else {
      this.tasSearchCache = cacheEntry;
    }

    return docs;
  }

  private get isTasDebugEnabled() {
    if (typeof window === 'undefined') {
      return false;
    }

    return (
      this.features.isEnabled('pslfDebugPanel') &&
      new URL(window.location.href).searchParams.get('debug') === 'true'
    );
  }

  private getEmptyTasData(): LiveTasData {
    return {
      isEligible: this.partner.hasNewTuitionAssistance,
      participantState: null,
      programs: [],
      applications: [],
      summary: {
        totalApproved: 0,
        totalPaid: 0,
        pendingCount: 0,
        activeCount: 0,
        completedCount: 0,
      },
      isLiveData: true,
    };
  }

  private getEmptyDemoTasData(): LiveTasData {
    return {
      ...this.getEmptyTasData(),
      isLiveData: false,
    };
  }

  /**
   * Returns a proxy object that mimics the TasApplicationModel interface for demo mode.
   * This prevents the show route from making a real API call for demo IDs like "demo-app-1".
   */
  getDemoTasApplicationProxy(applicationId: string): DemoTasApplicationProxy | null {
    const tasData = this.getDemoTasData();
    const demoApp = tasData.applications.find((app) => app.id === applicationId);
    if (!demoApp) {
      return null;
    }

    const demoProgram = tasData.programs[0] || null;

    return {
      id: demoApp.id,
      state: demoApp.state,
      paymentState: demoApp.paymentState,
      approvedTotal: demoApp.approvedTotal,
      createdAt: demoApp.createdAt,
      displayName: demoApp.name,
      activeCourses: [],
      tasCourses: [],
      tasAssets: [],
      tasProgramInstance: demoProgram
        ? {
            id: demoProgram.id,
            tasProgramTemplate: {
              programName: demoProgram.name,
              typeClassification: 'TAS.ProgramType.3',
              courseApprovalRequired: true,
            },
            fields: {},
          }
        : null,
      // Fields needed by the template
      fields: {
        APPLICATION_NAME: { values: [demoApp.name] },
        COURSES_BEGIN_DATE: { values: [demoApp.coursesBeginDate || ''] },
        COURSES_END_DATE: { values: [demoApp.coursesEndDate || ''] },
      },
      applicationName: demoApp.name,
      programName: demoApp.programName || '',
      coursesBeginDate: demoApp.coursesBeginDate || '',
      transitionLog: [],
      customFields: [],
      paymentStateLog: [],
      approvedTotalLog: [],
      coursesApproval: { approvers: [], status: 'pending' },
      evidenceApproval: { approvers: [], status: 'pending' },
      isDraft: demoApp.state === 'TAS.ApplicationState.DEFINE_COURSES',
      isApproved: demoApp.state === 'TAS.ApplicationState.COURSES_APPROVED',
      tags: [],
      attachments: [],
      _isDemoProxy: true,
    } as DemoTasApplicationProxy;
  }

  private getDemoTasData(): LiveTasData {
    const demoScenario = getCurrentDemoScenario();

    // If TA is not available in this scenario, return ineligible data
    if (!demoScenario.features.tuitionAssistance) {
      return {
        isEligible: false,
        participantState: null,
        programs: [],
        applications: [],
        summary: {
          totalApproved: 0,
          totalPaid: 0,
          pendingCount: 0,
          activeCount: 0,
          completedCount: 0,
        },
        isLiveData: false,
      };
    }

    const userState = getDemoUserState();
    const scenario = userState.benefitsMaxedOut ? 'completed' : getCurrentDemoTasScenarioId();
    const paymentModel = getCurrentDemoTaPaymentModelId();
    const isPrepayment = paymentModel === 'prepayment';
    const isDeferral = paymentModel === 'reimbursement-with-deferral';

    // Apply payment-model-specific projections to a TAS application: Prepayment
    // (CourseCash) preloads CourseCash totals to mirror funded course cards;
    // Reimbursement-with-deferral leaves payment pending until after course start.
    const projectApp = (app: LiveTasApplication): LiveTasApplication => {
      if (isPrepayment) {
        const isPending =
          app.paymentState === 'TAS.ApplicationPaymentState.NONE' && app.approvedTotal === 0;
        if (isPending) return app;
        return {
          ...app,
          paymentState: 'TAS.ApplicationPaymentState.PAID',
          paidTotal: app.approvedTotal,
          courseCashPreApprovedTotal: app.approvedTotal,
          courseCashFinalApprovedTotal: app.approvedTotal,
          courseCashUsed: app.approvedTotal,
          courseCashReserved: 0,
        };
      }
      if (isDeferral) {
        const isFulfilled = app.state === 'TAS.ApplicationState.FULFILLED';
        return {
          ...app,
          // Deferred reimbursement: payment is held until after course start, so
          // active applications show as not-yet-paid even when approved.
          paymentState: isFulfilled
            ? 'TAS.ApplicationPaymentState.PAID'
            : 'TAS.ApplicationPaymentState.NONE',
          paidTotal: isFulfilled ? app.approvedTotal : 0,
          courseCashPreApprovedTotal: null,
          courseCashFinalApprovedTotal: null,
          courseCashUsed: 0,
          courseCashReserved: 0,
        };
      }
      return app;
    };

    const baseProgram: LiveTasProgram = {
      id: 'demo-prog-1',
      name: isDeferral ? 'LPN to RN Bridge Program' : 'Data Analytics Certificate',
      state: 'TAS.ProgramInstanceState.IN_PROGRESS',
      institution: isDeferral
        ? 'Riverview LPN School (deferral-eligible)'
        : isPrepayment
          ? 'Coursera / Google (CourseCash)'
          : 'Coursera / Google',
      major: isDeferral ? 'Nursing' : 'Data Analytics',
      startDate: '2025-09-01',
      updatedAt: '2026-01-20',
      typeClassification: 'TAS.ProgramType.4.2',
      dependentId: null,
      dependentName: null,
    };

    const baseAppName = isDeferral ? 'Fall 2025 - LPN to RN Bridge' : 'Fall 2025 - Data Analytics';

    const baseApplication: LiveTasApplication = projectApp({
      id: 'demo-app-1',
      recordType: 'application',
      programInstanceId: 'demo-prog-1',
      programTemplateId: 'demo-tpl-1',
      name: baseAppName,
      state: 'TAS.ApplicationState.ATTEND',
      paymentState: 'TAS.ApplicationPaymentState.PAID_IN_PART',
      approvedTotal: 1500,
      requestedTotal: 1500,
      paidTotal: 750,
      courseCashPreApprovedTotal: null,
      courseCashFinalApprovedTotal: null,
      courseCashUsed: 0,
      courseCashReserved: 0,
      coursesBeginDate: '2025-09-01',
      coursesEndDate: '2025-12-15',
      createdAt: '2025-08-15',
      updatedAt: '2026-01-20',
      programName: baseProgram.name,
      dependentId: null,
      dependentName: null,
    });

    const scenarioData: Record<
      ReturnType<typeof getCurrentDemoTasScenarioId>,
      { programs: LiveTasProgram[]; applications: LiveTasApplication[] }
    > = {
      'not-started': {
        programs: [],
        applications: [],
      },
      'in-progress': {
        programs: [baseProgram],
        applications: [
          baseApplication,
          projectApp({
            id: 'demo-app-2',
            recordType: 'application',
            programInstanceId: 'demo-prog-1',
            programTemplateId: 'demo-tpl-1',
            name: 'AWS Certification',
            state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 0,
            requestedTotal: 0,
            paidTotal: 0,
            courseCashPreApprovedTotal: null,
            courseCashFinalApprovedTotal: null,
            courseCashUsed: 0,
            courseCashReserved: 0,
            coursesBeginDate: '2026-02-01',
            coursesEndDate: '2026-04-30',
            createdAt: '2026-01-20',
            updatedAt: '2026-01-20',
            programName: 'Professional Certifications',
            dependentId: null,
            dependentName: null,
          }),
        ],
      },
      approved: {
        programs: [baseProgram],
        applications: [
          projectApp({
            ...baseApplication,
            state: 'TAS.ApplicationState.COURSES_APPROVED',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 2400,
            requestedTotal: 2400,
            paidTotal: 0,
            courseCashPreApprovedTotal: null,
            courseCashFinalApprovedTotal: null,
            courseCashUsed: 0,
            courseCashReserved: 0,
            updatedAt: '2026-01-20',
          }),
        ],
      },
      rejected: {
        programs: [baseProgram],
        applications: [
          {
            ...baseApplication,
            state: 'TAS.ApplicationState.COURSES_REJECTED',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 0,
            requestedTotal: 1500,
            paidTotal: 0,
            courseCashPreApprovedTotal: null,
            courseCashFinalApprovedTotal: null,
            courseCashUsed: 0,
            courseCashReserved: 0,
            updatedAt: '2026-01-20',
          },
        ],
      },
      completed: {
        programs: [
          {
            ...baseProgram,
            state: 'TAS.ProgramInstanceState.COMPLETED',
          },
        ],
        applications: [
          projectApp({
            ...baseApplication,
            state: 'TAS.ApplicationState.FULFILLED',
            paymentState: 'TAS.ApplicationPaymentState.PAID',
            approvedTotal: 3000,
            requestedTotal: 3000,
            paidTotal: 3000,
            courseCashPreApprovedTotal: null,
            courseCashFinalApprovedTotal: null,
            courseCashUsed: 0,
            courseCashReserved: 0,
            updatedAt: '2026-01-20',
          }),
        ],
      },
    };

    const { programs, applications: rawApplications } = scenarioData[scenario];
    const applications = userState.benefitsMaxedOut
      ? rawApplications.map((app) => ({
          ...app,
          approvedTotal: DEMO_TAS_ANNUAL_LIMIT,
          requestedTotal: DEMO_TAS_ANNUAL_LIMIT,
          paidTotal: DEMO_TAS_ANNUAL_LIMIT,
          courseCashUsed: app.courseCashUsed ? DEMO_TAS_ANNUAL_LIMIT : app.courseCashUsed,
          courseCashReserved: 0,
        }))
      : rawApplications;

    const summary = {
      totalApproved: applications.reduce((sum, app) => sum + app.approvedTotal, 0),
      totalPaid: applications.reduce((sum, app) => sum + app.paidTotal, 0),
      pendingCount: applications.filter((app) =>
        [
          'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
          'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
        ].includes(app.state)
      ).length,
      activeCount: applications.filter((app) =>
        ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.COURSES_APPROVED'].includes(app.state)
      ).length,
      completedCount: applications.filter((app) => app.state === 'TAS.ApplicationState.FULFILLED')
        .length,
    };

    return {
      isEligible: true,
      participantState: 'TAS.ParticipantState.ACTIVE',
      programs,
      applications,
      summary,
      isLiveData: false,
    };
  }

  /**
   * Fetches comprehensive benefits summary combining SLR, TAS, and PSLF
   */
  async fetchBenefitsSummary() {
    const [contributionsData, tasData, pslfData, section127Info] = await Promise.all([
      this.fetchEmployerContributions(),
      this.fetchTasData(),
      this.fetchPslfData(),
      this.fetchSection127Info(),
    ]);

    return {
      section127: section127Info,
      slr: {
        enrolled: contributionsData.isLiveData && contributionsData.contributions.yearToDate > 0,
        yearToDate: contributionsData.contributions.yearToDate,
        lifetime: contributionsData.contributions.lifetime,
        isLiveData: contributionsData.isLiveData,
      },
      tas: {
        enrolled: tasData.applications.length > 0,
        ...tasData.summary,
        isLiveData: tasData.isLiveData,
      },
      pslf: {
        enrolled: pslfData.forms.length > 0,
        qualifyingPayments: Math.max(
          0,
          ...pslfData.accounts.flatMap((a) => a.loans.map((l) => l.qualifyingPayments ?? 0))
        ),
        estimatedForgiveness: pslfData.estimatorResults?.estimatedForgivenessAmount ?? 0,
        isLiveData: pslfData.isLiveData,
      },
      hasAnyBenefits:
        contributionsData.contributions.yearToDate > 0 ||
        tasData.applications.length > 0 ||
        pslfData.forms.length > 0,
    };
  }

  async fetchLrapDiscovery() {
    return this.careerPathwaysApi.fetchLrapDiscovery();
  }

  /**
   * Shared implementation for fetchEligibleTemplates and fetchDependentEligibleTemplates.
   * @param dependentOnly - if true, returns only dependent programs; if false, returns only employee programs.
   */
  private async fetchEligibleTemplatesInternal(
    dependentOnly: boolean,
    enrolledTemplateIds?: Set<string>
  ): Promise<EligibleTemplate[]> {
    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return [];
    }

    const companyId = employee.company?.id;
    if (!companyId) {
      return [];
    }

    try {
      const hasTas = this.partner.hasNewTuitionAssistance;
      if (!hasTas) {
        return [];
      }

      // byEligibility does NOT return is_trial templates — the scope's final SELECT requires
      // a tio_tas.eligibilities join which trial templates never have. Fetch them separately.
      const canViewTrialTemplates =
        this.sessionContext.user?.isEmployeeAdmin || this.sessionContext.user?.isAccountOwner;

      // WarpDrive builder reads (Phase 2.4) — replace store.query;
      // flattened bracket keys satisfy the builder's QueryParamsSource type.
      const [allTemplatesResult, trialTemplatesResult] = await Promise.all([
        this.store.request(
          query<TasProgramTemplateModel>('tas-program-template', {
            'filter[state]': 'TAS.ProgramTemplateState.LAUNCHED',
            'filter[byEligibility]': employee.id as string,
            'filter[company]': companyId,
            include: 'company.company-setting',
            reload: true,
            'page[limit]': 100,
          })
        ) as Promise<{ content: { data: TasProgramTemplateModel[] } }>,
        canViewTrialTemplates
          ? (this.store.request(
              query<TasProgramTemplateModel>('tas-program-template', {
                'filter[state]': 'TAS.ProgramTemplateState.LAUNCHED',
                'filter[isTrial]': true,
                'filter[company]': companyId,
                include: 'company.company-setting',
                reload: true,
                'page[limit]': 100,
              })
            ) as Promise<{ content: { data: TasProgramTemplateModel[] } }>)
          : Promise.resolve({ content: { data: [] as TasProgramTemplateModel[] } }),
      ]);

      // Trial templates bypass filterEligibleTemplates (they have no eligibility records).
      const trialTemplates = trialTemplatesResult.content.data;
      let templates = [
        ...allTemplatesResult.content.data,
      ] as unknown as typeof allTemplatesResult.content.data;

      // Get participant for eligibility checks
      let participant = employee.tasParticipant;
      if (!participant) {
        // WarpDrive builder read (Phase 2.4) — replaces store.findRecord.
        const { content: empContent3 } = (await this.store.request(
          findRecord<EmployeeModel>('employee', employee.id as string, {
            include: 'tas-participant,company',
            reload: true,
          })
        )) as { content: { data: EmployeeModel } };
        participant = empContent3.data?.tasParticipant;
      }

      const tasParticipantId = participant?.id;

      // Filter by dynamic eligibility and period of eligibility
      if (tasParticipantId) {
        const [dynamicResponse, periodsResponse] = await Promise.all([
          this.store.request(dynamicEligibilityRequest(tasParticipantId)),
          this.store.request(periodsOfEligibilityRequest(tasParticipantId)),
        ]);
        const dynamicEligibility = dynamicResponse.content as DynamicEligibility;
        const programEligibility = periodsResponse.content as PeriodOfEligibility;

        templates = filterEligibleTemplates(
          [...templates],
          dynamicEligibility,
          programEligibility
        ) as unknown as typeof templates;
      }

      // Merge trial templates in after the eligibility filter (they bypass it),
      // de-duplicating by template id to avoid duplicate benefit cards.
      const existingIds = new Set(
        [...templates].map((t) => t.id).filter((id): id is string => Boolean(id))
      );
      const dedupedTrialTemplates = trialTemplates.filter((t) => t.id && !existingIds.has(t.id));
      templates = [...templates, ...dedupedTrialTemplates] as unknown as typeof templates;

      const enrolledIds = enrolledTemplateIds ?? new Set<string>();

      return [...templates]
        .filter((t) => (dependentOnly ? t.isDependentProgram : !t.isDependentProgram))
        .map((t) => ({
          id: t.id || '',
          name: t.programName || 'Program',
          description: t.programDescription || null,
          isDirectPayProgram: t.isDirectPayProgram,
          isEnrolled: t.id ? enrolledIds.has(t.id) : false,
          isTrial: t.isTrial,
          typeClassification: t.typeClassification,
        }));
    } catch (error) {
      console.warn(
        `Failed to fetch ${dependentOnly ? 'dependent ' : ''}eligible templates:`,
        error
      );
      return [];
    }
  }

  /**
   * Fetches eligible program templates for the Eligible Benefits section.
   * In demo mode, returns static demo templates.
   * In live mode, queries for LAUNCHED templates filtered by employee eligibility.
   */
  async fetchEligibleTemplates(enrolledTemplateIds?: Set<string>): Promise<EligibleTemplate[]> {
    if (this.isDemoMode) {
      return this.getDemoEligibleTemplates();
    }
    return this.fetchEligibleTemplatesInternal(false, enrolledTemplateIds);
  }

  /**
   * Fetches eligible dependent program templates.
   * Filters to only dependent programs (inverse of fetchEligibleTemplates).
   */
  async fetchDependentEligibleTemplates(
    enrolledTemplateIds?: Set<string>
  ): Promise<EligibleTemplate[]> {
    if (this.isDemoMode) {
      return this.getDemoDependentEligibleTemplates(enrolledTemplateIds);
    }

    return this.fetchEligibleTemplatesInternal(true, enrolledTemplateIds);
  }

  private getDemoDependentEligibleTemplates(enrolledTemplateIds?: Set<string>): EligibleTemplate[] {
    const demoScenario = getCurrentDemoScenario();

    if (!demoScenario.features.tuitionAssistance) {
      return [];
    }

    return [
      {
        id: 'demo-dependent-tpl-1',
        name: 'Dependent Degree Completion Program',
        description:
          'Static demo dependent benefit for family-member tuition assistance scenarios.',
        isDirectPayProgram: false,
        isEnrolled: enrolledTemplateIds?.has('demo-dependent-tpl-1') ?? false,
        isTrial: false,
        typeClassification: 'TAS.ProgramType.4',
      },
    ];
  }

  private getDemoEligibleTemplates(): EligibleTemplate[] {
    const demoScenario = getCurrentDemoScenario();

    if (!demoScenario.features.tuitionAssistance) {
      return [];
    }

    return [
      {
        id: 'demo-tpl-1',
        name: 'Professional Certifications Program',
        description:
          'Earn industry-recognized certifications in your field. Covers exam fees, prep materials, and study courses.',
        isDirectPayProgram: false,
        isEnrolled: true,
        isTrial: false,
        typeClassification: 'TAS.ProgramType.4',
      },
      {
        id: 'demo-tpl-2',
        name: 'Degree Completion Program',
        description:
          "Complete your bachelor's or master's degree at an accredited institution with tuition reimbursement.",
        isDirectPayProgram: false,
        isEnrolled: false,
        isTrial: false,
        typeClassification: 'TAS.ProgramType.4',
      },
      {
        id: 'demo-tpl-3',
        name: 'Skills Development CourseCA$H',
        description:
          'Access pre-approved online courses and bootcamps with direct payment through CourseCA$H.',
        isDirectPayProgram: true,
        isEnrolled: false,
        isTrial: false,
        typeClassification: 'TAS.ProgramType.2',
      },
    ];
  }

  async fetchLrapPrograms() {
    return this.careerPathwaysApi.fetchLrapPrograms();
  }

  async fetchCareerFamilies() {
    return this.careerPathwaysApi.fetchCareerFamilies();
  }

  async fetchFeaturedRoles() {
    return this.careerPathwaysApi.fetchFeaturedRoles();
  }

  async fetchCareerRole(roleId: string) {
    return this.careerPathwaysApi.fetchCareerRole(roleId);
  }

  async fetchRolePathways(roleId: string) {
    return this.careerPathwaysApi.fetchRolePathways(roleId);
  }

  async fetchAllRolesForComparison() {
    return this.careerPathwaysApi.fetchAllRolesForComparison();
  }

  async fetchAssessmentQuestions(skillId: string) {
    return this.careerPathwaysApi.fetchAssessmentQuestions(skillId);
  }

  async fetchEmployeeSkills() {
    return this.careerPathwaysApi.fetchEmployeeSkills();
  }

  async fetchSkillGaps() {
    return this.careerPathwaysApi.fetchSkillGaps();
  }

  async fetchCareerPlan() {
    return this.careerPathwaysApi.fetchCareerPlan();
  }

  async fetchAvailableGoals() {
    return this.careerPathwaysApi.fetchAvailableGoals();
  }

  async fetchRecommendations() {
    return this.careerPathwaysApi.fetchRecommendations();
  }

  async fetchSuccessStories() {
    return this.careerPathwaysApi.fetchSuccessStories();
  }

  async fetchPslfVault() {
    return this.careerPathwaysApi.fetchPslfVault();
  }
}
